import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { AppUser } from 'Src/views/App/App';
import {
  Tournament,
  TournamentCompetitionDefinition,
  TournamentCompetitionRuntime,
  TournamentMatchSource,
  TournamentStructureMatch,
  TournamentTieBreaker
} from 'Src/models/tournament';
import {
  confirmTournamentGroupAdvancement,
  getTournamentCompetitionRuntime,
  publishTournamentCompetitionStructure,
  saveTournamentCompetitionStructureDraft,
  saveTournamentGroupOrder,
  previewTournamentCompetitionStructure,
  TournamentStructurePublishPreview,
  getTournament
} from 'Src/helpers/service/adminSU/tournamentsTable';
import { Autocomplete } from 'Src/components/Autocomplete/Autocomplete';
import { convertPlaceToPostcodeWithPlaceName, getPostcodes, isPlace } from 'Src/helpers/venue/venue';
import { getNameByPlaceOrPostcode } from 'Src/helpers/autocomplete/autocomplete';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DatePicker } from 'Src/components/DatePicker/DatePicker';
import { TimePicker } from 'Src/components/TimePicker/TimePicker';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { UnsavedChangesModal } from 'Src/components/UnsavedChangesModal/UnsavedChangesModal';
import './TournamentCompetitionStructure.scss';
import { SchoolMark, StructureTab, TournamentStructureWorkspace } from './TournamentStructureWorkspace';
import { initialStructureLayout, TournamentStructureGroupsBoard } from './TournamentStructureGroupsBoard';
import { TournamentInitialGroupFixtures } from './TournamentInitialGroupFixtures';
import { SchedulePosition, TournamentScheduleGrid } from './TournamentScheduleGrid';
import { structureId } from './structureId';
import { tournamentStructurePublicRoutes } from './tournamentStructurePublicRoutes';
import { RANKING_CRITERIA, TournamentRankingEditor } from './TournamentRankingEditor';
import { TournamentKnockoutBracket } from './TournamentKnockoutBracket';
import {
  applyGeneratedDefaults,
  BRACKET_ROUNDS,
  knockoutFixtures,
  roundRobinFixtures,
  roundRobinPairs,
  resizeKnockoutFixtures
} from './tournamentStructureFixtures';

interface Props {
  user: AppUser;
  tournament: Tournament;
  standalone?: boolean;
  onUpdated?: (tournament: Tournament) => void;
}
const TIE_BREAKERS = RANKING_CRITERIA;
const RANKING_SHORT_LABELS: Partial<Record<TournamentTieBreaker, string>> = {
  HEAD_TO_HEAD_POINTS: 'H2H',
  HEAD_TO_HEAD_GOAL_DIFFERENCE: 'H2H GD',
  HEAD_TO_HEAD_GOALS_FOR: 'H2H GF',
  GOAL_DIFFERENCE: 'GD',
  GOALS_FOR: 'GF',
  GOALS_AGAINST: 'Fewest GA',
  WINS: 'Wins',
  FAIR_PLAY: 'Fair play',
  MANUAL_DRAW: 'Manual'
};
const KNOCKOUT_ROUNDS = [
  { id: 'ROUND_OF_32', label: 'Round of 32 · 1/16 final' },
  { id: 'ROUND_OF_16', label: 'Round of 16 · 1/8 final' },
  { id: 'ROUND_OF_8', label: '1/8 final' },
  { id: 'QUARTER_FINAL', label: 'Quarter-final' },
  { id: 'SEMI_FINAL', label: 'Semi-final' },
  { id: 'FINAL', label: 'Final' },
  { id: 'PLAYOFF', label: 'Play-off / placing' }
];
const startRank = (match: TournamentStructureMatch) => {
  const time = Date.parse(match.startTime || '');
  return Number.isFinite(time) ? time : Infinity;
};
const byStartTime = (a: TournamentStructureMatch, b: TournamentStructureMatch) =>
  (startRank(a) - startRank(b)) || String(a.id || '').localeCompare(String(b.id || ''));
const emptyDefinition = (): TournamentCompetitionDefinition => ({
  scheduleDefaults: { groupDuration: 20, groupBreak: 5, knockoutDuration: 20, knockoutBreak: 5 },
  tieBreakers: [],
  matches: [],
  stageOrder: [],
  groupOrder: [],
  groupRoutes: [],
  manualRankings: []
});
const clone = (value?: TournamentCompetitionDefinition): TournamentCompetitionDefinition =>
  JSON.parse(JSON.stringify(value || emptyDefinition()));
const newId = () => Array.from({ length: 24 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
const sourceKey = (source: TournamentMatchSource) =>
  JSON.stringify([
    source.sourceType,
    source.sourceGroupId || '',
    source.sourcePosition || '',
    source.teamId || '',
    source.sourceMatchId || '',
    source.label || ''
  ]);
const semanticSourceKey = (source: TournamentMatchSource) =>
  JSON.stringify([
    source.sourceType,
    source.sourceGroupId || '',
    source.sourcePosition || '',
    source.teamId || '',
    source.sourceMatchId || ''
  ]);
const matchDuration = (match: TournamentStructureMatch) =>
  match.startTime && match.endTime
    ? Math.max(1, Math.min(180, Math.round((+new Date(match.endTime) - +new Date(match.startTime)) / 60000)))
    : 20;
const ordinalLabel = (position: number) =>
  `${position}${
    position % 100 >= 11 && position % 100 <= 13
      ? 'th'
      : position % 10 === 1
      ? 'st'
      : position % 10 === 2
      ? 'nd'
      : position % 10 === 3
      ? 'rd'
      : 'th'
  }`;
const structureVenue = (value: any) => {
  if (!value) return undefined;
  const venue = isPlace(value) ? convertPlaceToPostcodeWithPlaceName(value) : value;
  return {
    venueType: 'CUSTOM',
    postcodeId: venue.postcodeId || venue.id || venue.postcodeData?.id,
    placeId: venue.placeId || venue.placeData?.id,
    name: venue.name || venue.placeData?.name,
    postcode: venue.postcode || venue.postcodeData?.postcode
  };
};

export const TournamentCompetitionStructure: React.FC<Props> = ({
  user,
  tournament,
  standalone = false,
  onUpdated
}) => {
  const history = useHistory();
  const storedInitial = tournament.competitionStructure?.draft || tournament.competitionStructure?.published;
  const initial =
    !tournament.competitionStructure?.published &&
    !tournament.competitionStructure?.updatedAt &&
    !storedInitial?.matches?.length &&
    !storedInitial?.tieBreakers?.length
      ? {
          ...clone(storedInitial),
          tieBreakers: ([
            'HEAD_TO_HEAD_POINTS',
            'GOAL_DIFFERENCE',
            'GOALS_FOR',
            'GOALS_AGAINST',
            'WINS',
            'FAIR_PLAY',
            'MANUAL_DRAW'
          ] as TournamentTieBreaker[]).map(criterion => ({ criterion }))
        }
      : storedInitial;
  const [workspaceTab, setWorkspaceTab] = React.useState<StructureTab>('structure');
  const [open, setOpen] = React.useState(standalone);
  const [definition, setStoredDefinition] = React.useState<TournamentCompetitionDefinition>(() => clone(initial));
  const [selectedSource, setSelectedSource] = React.useState<TournamentMatchSource>();
  const [sourceQuery, setSourceQuery] = React.useState('');
  const [teamPickerQuery, setTeamPickerQuery] = React.useState('');
  const [publishPreview, setPublishPreview] = React.useState<TournamentStructurePublishPreview>();
  const [saving, setSaving] = React.useState(false);
  const [scheduleDirty, setScheduleDirty] = React.useState(false);
  const [scheduleSaving, setScheduleSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [blockedRemovalId, setBlockedRemovalId] = React.useState<string>();
  const [status, setStatus] = React.useState(tournament.competitionStructure?.status || 'DRAFT');
  const [savedDefinition, setSavedDefinition] = React.useState<TournamentCompetitionDefinition>(() => clone(initial));
  const dirty = JSON.stringify(definition) !== JSON.stringify(savedDefinition);
  const [hasPublished, setHasPublished] = React.useState(!!tournament.competitionStructure?.published);
  const setDefinition = (update: React.SetStateAction<TournamentCompetitionDefinition>) => {
    setStoredDefinition(current =>
      applyGeneratedDefaults(typeof update === 'function' ? update(current) : update, tournament)
    );
  };
  const [positionOverrides, setPositionOverrides] = React.useState<Record<string, number>>({});
  const [runtime, setRuntime] = React.useState<TournamentCompetitionRuntime>();
  const [runtimeLoading, setRuntimeLoading] = React.useState(false);
  const [openPanels, setOpenPanels] = React.useState<Record<string, boolean>>({});
  const [expandedMatches, setExpandedMatches] = React.useState<Record<string, boolean>>({});
  const matchEditorSnapshot = React.useRef<TournamentCompetitionDefinition>();
  const openMatchEditor = (id: string) => {
    matchEditorSnapshot.current = definition;
    setExpandedMatches({ [id]: true });
  };
  const closeMatchEditor = (discard = false) => {
    if (discard && matchEditorSnapshot.current) setStoredDefinition(matchEditorSnapshot.current);
    matchEditorSnapshot.current = undefined;
    setExpandedMatches({});
    setBlockedRemovalId(undefined);
  };
  const [recentlyAddedMatchId, setRecentlyAddedMatchId] = React.useState<string>();
  const [sourcesCollapsed, setSourcesCollapsed] = React.useState(false);
  const [editingSlot, setEditingSlot] = React.useState<{ matchId: string; slot: 'homeSource' | 'awaySource' }>();
  const [editingLocationMatchId, setEditingLocationMatchId] = React.useState<string>();
  const [activeBracketId, setActiveBracketId] = React.useState<string>();
  const [bracketView, setBracketView] = React.useState(false);
  const [activeRound, setActiveRound] = React.useState<string>();
  const [addStageOpen, setAddStageOpen] = React.useState(false);
  const structureGroups = definition.layout?.groups || initialStructureLayout(tournament, runtime).groups;
  const structureStages = definition.layout?.stages || tournament.tournamentLayout || [];
  const updateLayout = (
    change: (
      layout: NonNullable<TournamentCompetitionDefinition['layout']>
    ) => NonNullable<TournamentCompetitionDefinition['layout']>
  ) => {
    if (!runtime) {
      setError('Wait for tournament teams to load before editing the layout.');
      return;
    }
    setDefinition(current => {
      const previous = current.layout || initialStructureLayout(tournament, runtime);
      const layout = change(previous);
      const formatOf = (stage: any) =>
        stage?.format ||
        (stage?.type === 'GROUP' ||
        previous.groups.some(
          group => group.stageId === stage?.id && current.qualifierGroups?.some(pool => pool.groupId === group.id)
        )
          ? 'ROUND_ROBIN'
          : 'KNOCKOUT');
      const changedFormats = new Set(
        layout.stages
          .filter(stage => {
            const old = previous.stages.find(item => item.id === stage.id);
            return old && formatOf(old) !== formatOf(stage);
          })
          .map(stage => stage.id)
      );
      const affectedGroups = new Set<string>();
      for (const group of previous.groups) {
        const created =
          runtime.groups[group.id]?.createdMatches ||
          current.matches.some(
            match =>
              match.groupId === group.id && runtime.matches.some(item => item.matchId === match.id && item.eventId)
          );
        if (!created) continue;
        const before = previous.assignments.find(item => item.groupId === group.id)?.teamIds || [];
        const after = layout.assignments.find(item => item.groupId === group.id)?.teamIds || [];
        if (
          !layout.groups.some(item => item.id === group.id) ||
          changedFormats.has(group.stageId) ||
          before
            .slice()
            .sort()
            .join() !==
            after
              .slice()
              .sort()
              .join()
        ) {
          if ((group.roundRobinRounds || 1) > 1) {
            setError('This group uses multiple rounds. Redraw it through the existing group schedule in Fixtures.');
            return current;
          }
          affectedGroups.add(group.id);
        }
      }
      const ids = new Set(layout.groups.map(group => group.id));
      let next = {
        ...current,
        layout,
        stageOrder: layout.stages.map(stage => stage.id),
        groupOrder: (current.groupOrder || []).filter(id => ids.has(id)),
        groupRoutes: current.groupRoutes.filter(item => ids.has(item.groupId)),
        stageRules: current.stageRules?.filter(rule => layout.stages.some(stage => stage.id === rule.stageId)),
        manualRankings: current.manualRankings.filter(
          item => ids.has(item.groupId) && !affectedGroups.has(item.groupId)
        ),
        recreateGroupIds: Array.from(new Set([...(current.recreateGroupIds || []), ...Array.from(affectedGroups)])),
        matches: current.matches.filter(
          match =>
            ids.has(match.groupId) &&
            !changedFormats.has(previous.groups.find(group => group.id === match.groupId)?.stageId || '')
        ),
        qualifierGroups: (current.qualifierGroups || []).filter(
          pool =>
            ids.has(pool.groupId) &&
            !changedFormats.has(previous.groups.find(group => group.id === pool.groupId)?.stageId || '')
        )
      };
      layout.groups
        .filter(
          group =>
            layout.stages.some(stage => stage.id === group.stageId && stage.type === 'GROUP') &&
            (group.roundRobinRounds || 1) === 1 &&
            (!runtime.groups[group.id]?.createdMatches || affectedGroups.has(group.id))
        )
        .forEach((group, groupIndex) => {
          const sources = (layout.assignments.find(item => item.groupId === group.id)?.teamIds || []).map(teamId => ({
            sourceType: 'TEAM' as const,
            teamId
          }));
          const defaults = current.scheduleDefaults || {
            groupDuration: 20,
            groupBreak: 5,
            knockoutDuration: 20,
            knockoutBreak: 5
          };
          const old = next.matches.filter(match => match.groupId === group.id);
          const generated = roundRobinPairs(sources.length).map(([home, away], index) => {
            const existing = old.find(
              match =>
                !match.custom &&
                match.homeSource.teamId === sources[home].teamId &&
                match.awaySource.teamId === sources[away].teamId
            );
            const start =
              +new Date(tournament.startTime) + index * (defaults.groupDuration + defaults.groupBreak) * 60000;
            return (
              existing || {
                id: structureId(),
                generated: true,
                groupId: group.id,
                name: `${group.name} · Round robin ${index + 1}`,
                round: 'GROUP',
                homeSource: sources[home],
                awaySource: sources[away],
                startTime: new Date(start).toISOString(),
                endTime: new Date(start + defaults.groupDuration * 60000).toISOString(),
                pitchId: group.pitchId || tournament.pitches?.[groupIndex % (tournament.pitches?.length || 1)]?.id
              }
            );
          });
          next = {
            ...next,
            matches: [
              ...next.matches.filter(match => match.groupId !== group.id),
              ...generated,
              ...old.filter(match => match.custom)
            ],
            qualifierGroups: [
              ...next.qualifierGroups.filter(pool => pool.groupId !== group.id),
              ...(sources.length >= 2 ? [{ groupId: group.id, sources }] : [])
            ]
          };
        });
      layout.groups
        .filter(group => {
          const stage = layout.stages.find(item => item.id === group.stageId);
          const existingStage = previous.stages.some(item => item.id === group.stageId);
          const roundRobin = formatOf(stage) === 'ROUND_ROBIN';
          return (
            existingStage &&
            stage?.type !== 'GROUP' &&
            roundRobin &&
            (!previous.groups.some(item => item.id === group.id) || changedFormats.has(group.stageId)) &&
            !next.qualifierGroups.some(pool => pool.groupId === group.id)
          );
        })
        .forEach(group => {
          const sources = Array.from({ length: 4 }, (_, index) => ({
            sourceType: 'TEAM' as const,
            label: `Empty slot ${index + 1}`
          }));
          next = {
            ...next,
            qualifierGroups: [...next.qualifierGroups, { groupId: group.id, sources }],
            matches: [
              ...next.matches,
              ...roundRobinFixtures(
                group.id,
                group.name,
                sources,
                layout.stages.find(stage => stage.id === group.stageId)?.startTime || tournament.startTime,
                scheduleDefaults.groupDuration,
                scheduleDefaults.groupBreak
              )
            ]
          };
        });
      layout.groups
        .filter(
          group =>
            formatOf(layout.stages.find(stage => stage.id === group.stageId)) === 'KNOCKOUT' &&
            (!previous.groups.some(item => item.id === group.id) || changedFormats.has(group.stageId))
        )
        .forEach(group => {
          const oldPool = current.qualifierGroups?.find(pool => pool.groupId === group.id);
          const direct = (previous.assignments.find(item => item.groupId === group.id)?.teamIds || []).map(teamId => ({
            sourceType: 'TEAM' as const,
            teamId
          }));
          const sources: TournamentMatchSource[] = oldPool?.sources.length
            ? [...oldPool.sources]
            : direct.length
            ? direct
            : Array.from({ length: 4 }, (_, index) => ({
                sourceType: 'TEAM' as const,
                label: 'Empty slot ' + (index + 1)
              }));
          const first = [...BRACKET_ROUNDS].reverse().find(round => round.slots >= sources.length);
          if (!first) {
            setError('Knockout brackets support up to 32 entrants.');
            return;
          }
          const missing = first.slots - sources.length;
          for (let index = 0; index < missing; index++) sources.splice(index * 2 + 1, 0, { sourceType: 'BYE' });
          next = {
            ...next,
            matches: [
              ...next.matches,
              ...knockoutFixtures(
                group.id,
                group.name,
                first.id,
                sources,
                layout.stages.find(stage => stage.id === group.stageId)?.startTime || tournament.startTime,
                scheduleDefaults.knockoutDuration,
                scheduleDefaults.knockoutBreak
              )
            ]
          };
        });
      setError('');
      return next;
    });
  };
  const [editingQualifierId, setEditingQualifierId] = React.useState<string>();
  const [editingBracketId, setEditingBracketId] = React.useState<string>();
  const [bracketFirstRound, setBracketFirstRound] = React.useState('SEMI_FINAL');
  const [qualifierSources, setQualifierSources] = React.useState<TournamentMatchSource[]>([]);
  const [editingQualifierSlot, setEditingQualifierSlot] = React.useState<number>();
  React.useEffect(() => setTeamPickerQuery(''), [editingSlot, editingQualifierSlot]);
  const [qualifierTiming, setQualifierTiming] = React.useState({
    startTime: tournament.startTime,
    duration: 20,
    break: 5,
    pitchId: ''
  });
  const [pendingQualifierDefinition, setPendingQualifierDefinition] = React.useState<TournamentCompetitionDefinition>();
  const [replacingMatchId, setReplacingMatchId] = React.useState<string>();
  const [recreatingRound, setRecreatingRound] = React.useState(false);
  const [discardWarningOpen, setDiscardWarningOpen] = React.useState(false);
  const pendingNavigation = React.useRef<any>();
  const allowNavigation = React.useRef(false);
  const [manualOrders, setManualOrders] = React.useState<Record<string, string[]>>({});
  const manualRankingChanged = Object.keys(manualOrders).length > 0;
  const rankingPoints =
    definition.rankingPoints || [3, 1, 1, 0].map((fallback, index) => tournament.scoring?.[index]?.score ?? fallback);
  const scheduleDefaults = definition.scheduleDefaults || {
    groupDuration: 20,
    groupBreak: 5,
    knockoutDuration: 20,
    knockoutBreak: 5
  };
  const tournamentStartDate = new Date(tournament.startTime);
  const tournamentEndDate = new Date(tournament.endTime || tournament.startTime);
  const isSameStoredDate =
    String(tournament.startTime || '').slice(0, 10) ===
    String(tournament.endTime || tournament.startTime || '').slice(0, 10);
  const isSingleDay =
    isSameStoredDate ||
    (tournamentStartDate.getFullYear() === tournamentEndDate.getFullYear() &&
      tournamentStartDate.getMonth() === tournamentEndDate.getMonth() &&
      tournamentStartDate.getDate() === tournamentEndDate.getDate());
  const stageById = new Map(structureStages.map(stage => [stage.id, stage]));
  const groupStages = structureStages.filter(stage => stage.type === 'GROUP');
  const orderByIds = <T extends { id: string }>(
    items: T[],
    ids: string[] | undefined,
    fallback: (a: T, b: T) => number
  ) =>
    [...items].sort((a, b) => {
      const ai = ids?.indexOf(a.id) ?? -1;
      const bi = ids?.indexOf(b.id) ?? -1;
      if (ai >= 0 || bi >= 0) return (ai < 0 ? Number.MAX_SAFE_INTEGER : ai) - (bi < 0 ? Number.MAX_SAFE_INTEGER : bi);
      return fallback(a, b);
    });
  const knockoutStages = orderByIds(
    structureStages.filter(stage => stage.type !== 'GROUP'),
    definition.stageOrder,
    (a, b) => (a.order || 0) - (b.order || 0)
  );
  const groupStageIds = new Set(groupStages.map(stage => stage.id));
  const naturalGroups = [...structureGroups].sort(
    (a, b) =>
      (stageById.get(a.stageId)?.order || 0) - (stageById.get(b.stageId)?.order || 0) ||
      a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
  );
  const qualifierIds = new Set((definition.qualifierGroups || []).map(pool => pool.groupId));
  const treeGroups = structureStages
    .slice()
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .reduce<typeof naturalGroups>(
      (ordered, stage) =>
        ordered.concat(
          orderByIds(
            naturalGroups.filter(group => group.stageId === stage.id),
            definition.groupOrder,
            (a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
          )
        ),
      []
    );
  const sourceGroups = treeGroups.filter(group => groupStageIds.has(group.stageId) || qualifierIds.has(group.id));
  const liveGroups = runtime?.competitions || tournament.customGroups || [];
  const liveStages = runtime?.stages || tournament.tournamentLayout || [];
  const liveStructure = runtime?.structure || tournament.competitionStructure?.published;
  const livePools = new Set((liveStructure?.qualifierGroups || []).map(pool => pool.groupId));
  const liveSourceGroups = liveGroups.filter(
    group => liveStages.some(stage => stage.id === group.stageId && stage.type === 'GROUP') || livePools.has(group.id)
  );
  const knockoutGroups = orderByIds(
    naturalGroups.filter(group => !groupStageIds.has(group.stageId) || qualifierIds.has(group.id)),
    definition.groupOrder,
    (a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
  );
  const publicGroups = knockoutGroups.filter(group => !groupStageIds.has(group.stageId));
  const positionsForGroup = (groupId: string) => {
    const sources: TournamentMatchSource[] = [];
    definition.matches.forEach(match => sources.push(match.homeSource, match.awaySource));
    const referenced = Math.max(
      0,
      ...sources
        .filter(source => source.sourceType === 'GROUP_POSITION' && source.sourceGroupId === groupId)
        .map(source => Number(source.sourcePosition) || 0)
    );
    const pool = definition.qualifierGroups?.find(item => item.groupId === groupId);
    const assignedSize =
      definition.layout?.assignments.find(item => item.groupId === groupId)?.teamIds.length ??
      runtime?.teams.filter(team => team.groupIds.includes(groupId)).length ??
      0;
    return Array.from(
      {
        length: pool
          ? pool.sources.filter(source => source.sourceType !== 'BYE').length
          : Math.max(assignedSize, referenced, positionOverrides[groupId] || 0, 2)
      },
      (_, index) => index + 1
    );
  };
  const refreshRuntime = async () => {
    setRuntimeLoading(true);
    try {
      const value = await getTournamentCompetitionRuntime(user, tournament.id, true);
      setRuntime(value);
      if (value.isPublished !== undefined) setHasPublished(value.isPublished);
    } catch (error) {
      setError('The tournament standings could not be loaded. Try refreshing the page.');
    } finally {
      setRuntimeLoading(false);
    }
  };
  React.useEffect(() => {
    if (open) void refreshRuntime();
  }, [open, tournament.id]);
  React.useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (!dirty && !manualRankingChanged && !scheduleDirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, manualRankingChanged, scheduleDirty]);
  React.useEffect(
    () =>
      history.block(location => {
        if (!allowNavigation.current && (dirty || manualRankingChanged || scheduleDirty)) {
          pendingNavigation.current = location;
          setDiscardWarningOpen(true);
          return false;
        }
      }),
    [history, dirty, manualRankingChanged, scheduleDirty]
  );
  React.useEffect(() => {
    if (!recentlyAddedMatchId) return;
    const frame = window.requestAnimationFrame(() => {
      const card = document.querySelector(`[data-structure-match-id="${recentlyAddedMatchId}"]`);
      if (card instanceof HTMLElement) {
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
        card.focus({ preventScroll: true });
      }
    });
    const timer = window.setTimeout(() => setRecentlyAddedMatchId(undefined), 1800);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [recentlyAddedMatchId, definition.matches]);
  const matchRound = (match: TournamentStructureMatch) =>
    match.round ||
    ({ QUALIFIER: 'QUARTER_FINAL', PLAYOFF: 'SEMI_FINAL', FINAL: 'FINAL' } as any)[
      stageById.get(structureGroups.find(group => group.id === match.groupId)?.stageId || '')?.type || ''
    ] ||
    'PLAYOFF';
  const roundLabel = (round: string) =>
    round === 'GROUP' ? 'Round robin' : KNOCKOUT_ROUNDS.find(item => item.id === round)?.label || round;
  const dynamicMatchName = (match: TournamentStructureMatch, matches = definition.matches) => {
    const group = structureGroups.find(item => item.id === match.groupId);
    const round = matchRound(match);
    const position =
      matches
        .filter(item => item.groupId === match.groupId && matchRound(item) === round && !item.removed && !isByeMatch(item))
        .sort(byStartTime)
        .findIndex(item => item.id === match.id) + 1;
    return `${group?.name || 'Match'} · ${roundLabel(round)} ${Math.max(1, position)}`;
  };
  const fixtureDisplayName = (match: TournamentStructureMatch) =>
    match.custom || match.removed || isByeMatch(match) ? match.name : dynamicMatchName(match);
  const matchName = (id?: string) => {
    const match = definition.matches.find(item => item.id === id);
    return match ? fixtureDisplayName(match) : 'Previous match';
  };
  const isByeMatch = (match: TournamentStructureMatch) =>
    match.homeSource?.sourceType === 'BYE' || match.awaySource?.sourceType === 'BYE';
  const removeMatch = (match: TournamentStructureMatch) => {
    if (saving || !match.id) return;
    const usedByAnotherMatch = definition.matches.some(
      item =>
        item.id !== match.id &&
        [item.homeSource, item.awaySource].some(
          source =>
            (source?.sourceType === 'MATCH_WINNER' || source?.sourceType === 'MATCH_LOSER') &&
            source.sourceMatchId === match.id
        )
    );
    if (usedByAnotherMatch && !match.generated) {
      setBlockedRemovalId(match.id);
      return;
    }
    setError('');
    setBlockedRemovalId(undefined);
    setDefinition(current => ({
      ...current,
      matches:
        match.generated && !match.custom
          ? current.matches.map(item => (item.id === match.id ? { ...item, removed: true } : item))
          : current.matches.filter(item => item.id !== match.id)
    }));
    return true;
  };
  const teamIdentity = (teamId: string) => {
    const team = runtime?.teams.find(item => item.id === teamId);
    const school = (tournament.schools || []).find(item => String(item.id) === String(team?.schoolId));
    const schoolName = school?.name || team?.name || 'Tournament team';
    const multiple = (runtime?.teams || []).filter(item => item.schoolId === team?.schoolId).length > 1;
    return {
      teamName: team?.name || 'Tournament team',
      schoolName,
      schoolPic: school?.pic,
      displayName: schoolName + (multiple && team?.name ? ' · ' + team.name : '')
    };
  };
  const resolvedTeamIdentity = (matchId: string | undefined, slot: 'home' | 'away') => {
    const current = definition.matches.find(match => match.id === matchId);
    const published = tournament.competitionStructure?.published?.matches.find(match => match.id === matchId);
    const field = slot === 'home' ? 'homeSource' : 'awaySource';
    if (current && published && sourceKey(current[field]) !== sourceKey(published[field])) return undefined;
    const item = runtime?.matches.find(match => match.matchId === matchId);
    const teamId = slot === 'home' ? item?.homeTeamId : item?.awayTeamId;
    return teamId ? teamIdentity(teamId) : undefined;
  };
  const sourceTeamIdentity = (source: TournamentMatchSource) => {
    if (source.sourceType === 'TEAM') return source.teamId ? teamIdentity(source.teamId) : undefined;
    if (source.sourceType === 'GROUP_POSITION') {
      const group = runtime?.groups[source.sourceGroupId || ''];
      const row = group?.isConfirmed
        ? group.standings.find(item => item.position === source.sourcePosition && item.isResolved)
        : undefined;
      return row ? teamIdentity(row.teamId) : undefined;
    }
    for (const match of definition.matches) {
      for (const slot of ['homeSource', 'awaySource'] as const)
        if (sourceKey(match[slot]) === sourceKey(source)) {
          const team = resolvedTeamIdentity(match.id, slot === 'homeSource' ? 'home' : 'away');
          if (team) return team;
        }
    }
    return undefined;
  };
  const sourceLabel = (source?: TournamentMatchSource) =>
    !source
      ? 'Drop source here'
      : source.label ||
        (source.sourceType === 'BYE'
          ? 'Bye'
          : source.sourceType === 'GROUP_POSITION'
          ? `${source.sourcePosition}${
              source.sourcePosition === 1
                ? 'st'
                : source.sourcePosition === 2
                ? 'nd'
                : source.sourcePosition === 3
                ? 'rd'
                : 'th'
            } · ${structureGroups.find(group => group.id === source.sourceGroupId)?.name || 'Group'}`
          : source.sourceType === 'TEAM'
          ? source.teamId
            ? teamIdentity(source.teamId).displayName
            : 'Empty slot'
          : `${source.sourceType === 'MATCH_WINNER' ? 'Winner' : 'Loser'} · ${matchName(source.sourceMatchId)}`);
  const layoutForPoolSources = (groupId: string, sources: TournamentMatchSource[]) => {
    const group = structureGroups.find(item => item.id === groupId);
    if (!group || !groupStageIds.has(group.stageId)) return definition.layout;
    const layout = definition.layout || initialStructureLayout(tournament, runtime);
    const teamIds = sources
      .filter(source => source.sourceType === 'TEAM' && source.teamId)
      .map(source => source.teamId!);
    const other = layout.assignments.find(
      item =>
        item.groupId !== groupId &&
        layout.groups.some(entry => entry.id === item.groupId && entry.stageId === group.stageId) &&
        item.teamIds.some(id => teamIds.includes(id))
    );
    if (other) {
      setError('This team is already assigned to another group in this stage. Move it through Groups & teams.');
      return null;
    }
    return {
      ...layout,
      assignments: [...layout.assignments.filter(item => item.groupId !== groupId), { groupId, teamIds }]
    };
  };
  const setSlot = (matchId: string, slot: 'homeSource' | 'awaySource', source: TournamentMatchSource) => {
    if (saving) return;
    const isOwnMatchSource =
      (source.sourceType === 'MATCH_WINNER' || source.sourceType === 'MATCH_LOSER') &&
      String(source.sourceMatchId) === String(matchId);
    if (isOwnMatchSource) {
      setError('A match cannot use its own winner or loser as an opponent. Choose an earlier match.');
      setSelectedSource(undefined);
      return;
    }
    const match = definition.matches.find(item => item.id === matchId);
    if (match && semanticSourceKey(match[slot]) === semanticSourceKey(source)) {
      setSelectedSource(undefined);
      setEditingSlot(undefined);
      return;
    }
    if (
      match &&
      ((source.sourceType === 'BYE' &&
        match[slot === 'homeSource' ? 'awaySource' : 'homeSource'].sourceType === 'BYE') ||
        sourceKey(source) === sourceKey(match[slot === 'homeSource' ? 'awaySource' : 'homeSource']))
    ) {
      setError('Choose two different participants; a match cannot contain two byes.');
      return;
    }
    if (source.sourceType === 'GROUP_POSITION' && !earlierSourceGroup(source.sourceGroupId || '', match?.groupId)) {
      setError('Choose a finishing place from an earlier stage.');
      return;
    }
    if (
      (source.sourceType === 'MATCH_WINNER' || source.sourceType === 'MATCH_LOSER') &&
      !canUseMatchSource(source, match?.groupId, match?.round)
    ) {
      setError('Choose a match from an earlier stage, or a non-final loser from this bracket for a placing match.');
      return;
    }
    const pool = definition.qualifierGroups?.find(item => item.groupId === match?.groupId);
    if (pool && match && !match.custom) {
      const oldKey = sourceKey(match[slot]);
      if (
        source.sourceType !== 'BYE' &&
        pool.sources.some(item => sourceKey(item) !== oldKey && sourceKey(item) === sourceKey(source))
      ) {
        setError('This source is already in the qualifier group.');
        return;
      }
      const sources = pool.sources.map(entry => (sourceKey(entry) === oldKey ? source : entry));
      const layout = layoutForPoolSources(pool.groupId, sources);
      if (layout === null) return;
      const next = {
        ...definition,
        layout,
        recreateGroupIds: Array.from(new Set([...(definition.recreateGroupIds || []), pool.groupId])),
        qualifierGroups: definition.qualifierGroups?.map(item =>
          item.groupId === pool.groupId
            ? { ...item, sources: item.sources.map(entry => (sourceKey(entry) === oldKey ? source : entry)) }
            : item
        ),
        manualRankings: definition.manualRankings.filter(item => item.groupId !== pool.groupId),
        matches: definition.matches
          .map(item =>
            item.groupId === pool.groupId
              ? {
                  ...item,
                  homeSource: sourceKey(item.homeSource) === oldKey ? source : item.homeSource,
                  awaySource: sourceKey(item.awaySource) === oldKey ? source : item.awaySource
                }
              : item
          )
          .filter(
            item =>
              item.groupId !== pool.groupId ||
              item.homeSource.sourceType !== 'BYE' ||
              item.awaySource.sourceType !== 'BYE'
          )
      };
      setDefinition(next);
    } else
      setDefinition(current => ({
        ...current,
        matches: current.matches.map(item => (item.id === matchId ? { ...item, [slot]: source } : item))
      }));
    setSelectedSource(undefined);
    setEditingSlot(undefined);
    setError('');
  };
  const pickSource = (source: TournamentMatchSource) => {
    if (editingQualifierSlot !== undefined) {
      if (source.sourceType === 'BYE') source = { ...source, label: `Bye · slot ${editingQualifierSlot + 1}` };
      if (
        source.sourceType !== 'BYE' &&
        qualifierSources.some((item, index) => index !== editingQualifierSlot && sourceKey(item) === sourceKey(source))
      ) {
        setError('This source is already selected.');
        return;
      }
      setQualifierSources(current => {
        const next = [...current];
        next[editingQualifierSlot] = source;
        return next;
      });
      setEditingQualifierSlot(undefined);
      setError('');
    } else if (editingSlot) setSlot(editingSlot.matchId, editingSlot.slot, source);
  };
  const sourceChip = (source: TournamentMatchSource, label: React.ReactNode) => (
    <button
      key={sourceKey(source)}
      type="button"
      draggable
      className={selectedSource && sourceKey(selectedSource) === sourceKey(source) ? 'is-selected' : ''}
      onDragStart={event => {
        event.stopPropagation();
        event.dataTransfer.clearData();
        event.dataTransfer.setData('application/x-tournament-source', sourceKey(source));
      }}
      onClick={event => {
        event.stopPropagation();
        setSelectedSource(source);
      }}
    >
      {label}
    </button>
  );
  const addMatch = (groupId: string, round = 'QUARTER_FINAL') => {
    const group = knockoutGroups.find(item => item.id === groupId);
    if (!group) {
      setError('Create a group for this tournament stage first.');
      return;
    }
    const id = newId();
    const count = definition.matches.filter(match => match.groupId === group.id).length;
    const scheduled = definition.matches
      .filter(match => match.startTime && match.endTime)
      .sort((a, b) => +new Date(b.endTime!) - +new Date(a.endTime!))[0];
    const start = scheduled
      ? new Date(+new Date(scheduled.endTime!) + (round === 'GROUP' ? scheduleDefaults.groupBreak : scheduleDefaults.knockoutBreak) * 60000)
      : new Date(tournament.startTime);
    const directTeams = runtime?.teams || [];
    const entrants = definition.qualifierGroups?.find(pool => pool.groupId === groupId)?.sources;
    const homeSource: TournamentMatchSource =
      entrants?.[0] ||
      (sourceGroups.length
        ? { sourceType: 'GROUP_POSITION', sourceGroupId: sourceGroups[0]?.id, sourcePosition: 1 }
        : { sourceType: 'TEAM', teamId: directTeams[0]?.id });
    const awaySource: TournamentMatchSource =
      entrants?.[1] ||
      (sourceGroups.length
        ? { sourceType: 'GROUP_POSITION', sourceGroupId: sourceGroups[0]?.id, sourcePosition: 2 }
        : { sourceType: 'TEAM', teamId: directTeams[1]?.id });
    const next = {
      id,
      custom: true,
      name: `${group.name} ${count + 1}`,
      groupId: group.id,
      round,
      pitchId: group.pitchId,
      startTime: start.toISOString(),
      endTime: new Date(+start + (round === 'GROUP' ? scheduleDefaults.groupDuration : scheduleDefaults.knockoutDuration) * 60000).toISOString(),
      homeSource,
      awaySource
    };
    setDefinition(current => ({ ...current, matches: [...current.matches, next] }));
    openMatchEditor(id);
    setActiveBracketId(group.id);
    setActiveRound(round);
    setRecentlyAddedMatchId(id);
    setError('');
  };
  const patchMatch = (id: string, patch: Partial<TournamentStructureMatch>) =>
    setDefinition(current => ({
      ...current,
      matches: current.matches.map(match => (match.id === id ? { ...match, ...patch } : match))
    }));
  const setMatchStart = (match: TournamentStructureMatch, value: Date | null) => {
    if (!value) {
      patchMatch(match.id!, { startTime: undefined, endTime: undefined, timeOverride: true });
      return;
    }
    const start = new Date(value);
    patchMatch(match.id!, {
      startTime: start.toISOString(),
      endTime: new Date(+start + matchDuration(match) * 60000).toISOString(),
      timeOverride: true
    });
  };
  const setMatchDate = (match: TournamentStructureMatch, value: Date | null) => {
    if (!value) return;
    const current = match.startTime ? new Date(match.startTime) : new Date(tournament.startTime);
    current.setFullYear(value.getFullYear(), value.getMonth(), value.getDate());
    setMatchStart(match, current);
  };
  const setMatchTime = (match: TournamentStructureMatch, value: Date | null) => {
    if (!value) return;
    const current = match.startTime ? new Date(match.startTime) : new Date(tournament.startTime);
    const tournamentDate = new Date(tournament.startTime);
    if (isSingleDay)
      current.setFullYear(tournamentDate.getFullYear(), tournamentDate.getMonth(), tournamentDate.getDate());
    current.setHours(value.getHours(), value.getMinutes(), 0, 0);
    setMatchStart(match, current);
  };
  const setMatchDuration = (match: TournamentStructureMatch, value: number) => {
    const duration = Math.max(1, Math.min(180, value || 1));
    patchMatch(match.id!, {
      endTime: match.startTime ? new Date(+new Date(match.startTime) + duration * 60000).toISOString() : undefined,
      durationOverride: true
    });
  };
  const setManualOrder = (groupId: string, teamIds: string[]) => {
    setManualOrders(current => ({ ...current, [groupId]: teamIds }));
  };
  const moveManualTeam = (groupId: string, teamIds: string[], index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= teamIds.length) return;
    const next = [...teamIds];
    [next[index], next[target]] = [next[target], next[index]];
    setManualOrder(groupId, next);
  };
  const displayedGroupStandings = (groupId: string) => {
    const standings = runtime?.groups[groupId]?.standings || [];
    const manualIds = manualOrders[groupId] || [];
    if (!manualIds.length) return standings.map(row => ({ ...row, isManualOrder: false }));
    const byTeamId = new Map(standings.map(row => [row.teamId, row]));
    const orderedIds = [
      ...manualIds,
      ...standings.map(row => row.teamId).filter(teamId => !manualIds.includes(teamId))
    ];
    return orderedIds
      .map((teamId, index) =>
        byTeamId.get(teamId) ? { ...byTeamId.get(teamId)!, position: index + 1, isManualOrder: true } : undefined
      )
      .filter(Boolean) as Array<typeof standings[number] & { isManualOrder: boolean }>;
  };
  const moveDisplayItem = (field: 'stageOrder' | 'groupOrder', visibleIds: string[], id: string, direction: -1 | 1) =>
    setDefinition(current => {
      const saved = current[field] || [];
      const order = [
        ...saved.filter(item => visibleIds.includes(item)),
        ...visibleIds.filter(item => !saved.includes(item))
      ];
      const index = order.indexOf(id),
        target = index + direction;
      if (index < 0 || target < 0 || target >= order.length) return current;
      [order[index], order[target]] = [order[target], order[index]];
      return { ...current, [field]: [...saved.filter(item => !visibleIds.includes(item)), ...order] };
    });
  const configureQualifier = (groupId: string) => {
    setEditingQualifierId(groupId);
    setQualifierSources([...(definition.qualifierGroups?.find(pool => pool.groupId === groupId)?.sources || [])]);
    const matches = definition.matches
      .filter(match => match.groupId === groupId && !isByeMatch(match))
      .sort((a, b) => +new Date(a.startTime || tournament.startTime) - +new Date(b.startTime || tournament.startTime));
    const first = matches[0];
    setQualifierTiming({
      startTime: first?.startTime || tournament.startTime,
      duration: first ? matchDuration(first) : scheduleDefaults.knockoutDuration,
      break:
        first?.endTime && matches[1]?.startTime
          ? Math.max(0, Math.round((+new Date(matches[1].startTime) - +new Date(first.endTime)) / 60000))
          : scheduleDefaults.knockoutBreak,
      pitchId: first?.pitchId || ''
    });
  };
  const qualifierLocked = (groupId: string) =>
    definition.matches.some(
      match =>
        match.groupId === groupId &&
        (match.eventId || runtime?.matches.find(item => item.matchId === match.id)?.eventId)
    );
  const applyQualifier = () => {
    if (!editingQualifierId || qualifierSources.filter(source => source.sourceType !== 'BYE').length < 2) return;
    const groupId = editingQualifierId;
    const group = structureGroups.find(item => item.id === groupId);
    const fixtures: TournamentStructureMatch[] = [];
    const existing = definition.matches.filter(match => match.groupId === groupId);
    const start = +new Date(qualifierTiming.startTime);
    if (
      !Number.isFinite(start) ||
      !Number.isInteger(qualifierTiming.duration) ||
      qualifierTiming.duration < 1 ||
      qualifierTiming.duration > 180 ||
      !Number.isInteger(qualifierTiming.break) ||
      qualifierTiming.break < 0 ||
      qualifierTiming.break > 180
    ) {
      setError('Enter a valid start time, duration (1–180 min) and break (0–180 min).');
      return;
    }
    for (const [home, away] of roundRobinPairs(qualifierSources.length)) {
      const homeSource = qualifierSources[home],
        awaySource = qualifierSources[away];
      if (homeSource.sourceType === 'BYE' && awaySource.sourceType === 'BYE') continue;
      const previous = existing.find(
        match =>
          sourceKey(match.homeSource) === sourceKey(homeSource) && sourceKey(match.awaySource) === sourceKey(awaySource)
      );
      const realCount = fixtures.filter(
        item => item.homeSource.sourceType !== 'BYE' && item.awaySource.sourceType !== 'BYE'
      ).length;
      const time = start + realCount * (qualifierTiming.duration + qualifierTiming.break) * 60000;
      fixtures.push({
        ...previous,
        generated: previous?.generated ?? true,
        timeOverride:
          !!previous?.timeOverride ||
          start !== +new Date(stageById.get(group?.stageId || '')?.startTime || tournament.startTime) ||
          qualifierTiming.duration !== scheduleDefaults.groupDuration ||
          qualifierTiming.break !== scheduleDefaults.groupBreak,
        durationOverride: !!previous?.durationOverride || qualifierTiming.duration !== scheduleDefaults.groupDuration,
        pitchOverride:
          !!previous?.pitchOverride ||
          qualifierTiming.pitchId !==
            (group?.pitchMode === 'NONE'
              ? ''
              : group?.pitchId ||
                tournament.pitches?.[
                  naturalGroups.filter(item => item.stageId === group?.stageId).findIndex(item => item.id === groupId) %
                    (tournament.pitches?.length || 1)
                ]?.id ||
                ''),
        id: previous?.id || newId(),
        name: `${group?.name} · Round robin ${fixtures.length + 1}`,
        groupId,
        round: 'GROUP',
        homeSource,
        awaySource,
        startTime: new Date(time).toISOString(),
        endTime: new Date(time + qualifierTiming.duration * 60000).toISOString(),
        pitchId: qualifierTiming.pitchId || undefined
      });
    }
    const next = {
      ...definition,
      layout: layoutForPoolSources(groupId, qualifierSources),
      recreateGroupIds:
        definition.qualifierGroups
          ?.find(pool => pool.groupId === groupId)
          ?.sources.map(semanticSourceKey)
          .sort()
          .join('|') !==
        qualifierSources
          .map(semanticSourceKey)
          .sort()
          .join('|')
          ? Array.from(new Set([...(definition.recreateGroupIds || []), groupId]))
          : definition.recreateGroupIds,
      qualifierGroups: [
        ...(definition.qualifierGroups || []).filter(pool => pool.groupId !== groupId),
        { groupId, sources: qualifierSources }
      ],
      matches: [...definition.matches.filter(match => match.groupId !== groupId), ...fixtures],
      manualRankings: definition.manualRankings.filter(item => item.groupId !== groupId)
    };
    if (next.layout === null) return;
    setDefinition(next);
    setEditingQualifierId(undefined);
    setActiveRound('GROUP');
  };
  const applySchedule = (current: TournamentCompetitionDefinition, positions: Record<string, SchedulePosition>) => ({
    ...current,
    matches: current.matches.map(match => {
      const position = positions[match.id || ''];
      return position ? { ...match, ...position, pitchId: position.pitchId || undefined,
        timeOverride: true, pitchOverride: true } : match;
    })
  });
  const save = async (publish: boolean, nextDefinition = definition) => {
    if (!runtime) {
      setError('Wait for the tournament teams to load before saving.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const normalized = {
        ...nextDefinition,
        editorVersion: 2 as const,
        layout: nextDefinition.layout || initialStructureLayout(tournament, runtime),
        groupRoutes: tournamentStructurePublicRoutes(nextDefinition, naturalGroups),
        matches: nextDefinition.matches.map(match => ({
          ...match,
          name: match.custom || match.removed || isByeMatch(match) ? match.name : dynamicMatchName(match, nextDefinition.matches)
        }))
      };
      const updated = await saveTournamentCompetitionStructureDraft(user, tournament.id, normalized);
      if (onUpdated) onUpdated(updated);
      const saved = clone(updated?.competitionStructure?.draft || normalized);
      setSavedDefinition(saved);
      setStoredDefinition(current => (JSON.stringify(current) === JSON.stringify(definition) ? saved : current));
      setStatus('DRAFT');
      if (publish) setPublishPreview(await previewTournamentCompetitionStructure(user, tournament.id));
      return true;
    } catch (e) {
      setError(e?.response?.data?.details?.text || e?.response?.data?.message || 'The structure could not be saved.');
    } finally {
      setSaving(false);
    }
  };
  const publishReviewed = async () => {
    if (!publishPreview) return;
    setSaving(true);
    setError('');
    try {
      const updated = await publishTournamentCompetitionStructure(user, tournament.id, publishPreview.token);
      if (onUpdated) onUpdated(updated);
      const saved = clone(
        updated?.competitionStructure?.published || { ...definition, recreateGroupIds: [], recreateMatchIds: [] }
      );
      setStoredDefinition(saved);
      setSavedDefinition(saved);
      setHasPublished(true);
      setStatus('PUBLISHED');
      setPublishPreview(undefined);
      setRuntime(await getTournamentCompetitionRuntime(user, tournament.id, true));
    } catch (e) {
      setError(e?.response?.data?.details?.text || 'The structure could not be published. Review the impact again.');
      setPublishPreview(undefined);
    } finally {
      setSaving(false);
    }
  };
  const discardAndLeave = () => {
    setDiscardWarningOpen(false);
    allowNavigation.current = true;
    if (pendingNavigation.current) {
      history.push(pendingNavigation.current);
      return;
    }
    standalone
      ? history.push({ pathname: '/tournaments/manage/competition/teams', search: `tournament=${tournament.id}` })
      : setOpen(false);
  };
  const leave = () => {
    if (dirty || manualRankingChanged || scheduleDirty) {
      setDiscardWarningOpen(true);
      return;
    }
    discardAndLeave();
  };
  const confirmGroupAdvancement = async (groupIds: string[], selectedOrder?: string[]) => {
    setSaving(true);
    setError('');
    try {
      if (!hasPublished) throw new Error('Publish the structure before confirming groups.');
      const groupId = groupIds[0];
      const order = selectedOrder || manualOrders[groupId];
      if (groupIds.length === 1 && order) {
        const reviewedRuntime = await saveTournamentGroupOrder(
          user,
          tournament.id,
          groupId,
          order,
          runtime?.groups[groupId]?.resultHash || ''
        );
        setRuntime(reviewedRuntime);
        setStoredDefinition(current => ({
          ...current,
          manualRankings: [
            ...current.manualRankings.filter(item => item.groupId !== groupId),
            { groupId, teamIds: order }
          ]
        }));
        setSavedDefinition(current => ({
          ...current,
          manualRankings: [
            ...current.manualRankings.filter(item => item.groupId !== groupId),
            { groupId, teamIds: order }
          ]
        }));
      } else {
        const groupResultHashes = groupIds.reduce<Record<string, string>>((hashes, id) => {
          const resultHash = runtime?.groups[id]?.resultHash;
          if (!resultHash) throw new Error('Refresh the standings before confirming qualification.');
          hashes[id] = resultHash;
          return hashes;
        }, {});
        await confirmTournamentGroupAdvancement(user, tournament.id, groupIds, groupResultHashes);
      }
      setRuntime(await getTournamentCompetitionRuntime(user, tournament.id, true));
      setManualOrders(current => {
        const next = { ...current };
        delete next[groupId];
        return next;
      });
      setOpenPanels(current => ({ ...current, [`order-${groupId}`]: false }));
      if (onUpdated) onUpdated(await getTournament(user, tournament.id));
    } catch (e) {
      setError(e?.response?.data?.details?.text || e?.message || 'Group qualification could not be confirmed.');
      await refreshRuntime();
    } finally {
      setSaving(false);
    }
  };
  const materializeMatch = async (match: TournamentStructureMatch, replace = false) => {
    if (replace) {
      setDefinition(current => ({
        ...current,
        recreateMatchIds: Array.from(new Set([...(current.recreateMatchIds || []), match.id!]))
      }));
      setReplacingMatchId(undefined);
    } else await save(true);
  };
  const recreateSelectedRound = () => {
    if (!activeBracket) return;
    const ids = definition.matches
      .filter(match => match.groupId === activeBracket.id && matchRound(match) === selectedRound)
      .map(match => match.id!);
    const next = pendingQualifierDefinition || definition;
    const pool = next.qualifierGroups?.find(item => item.groupId === activeBracket.id);
    if (pool || next.matches.some(match => match.groupId === activeBracket.id && match.generated)) {
      const old = next.matches.filter(match => match.groupId === activeBracket.id);
      const generated = pool
        ? roundRobinFixtures(
            activeBracket.id,
            activeBracket.name,
            pool.sources,
            stageById.get(activeBracket.stageId)?.startTime || tournament.startTime,
            scheduleDefaults.groupDuration,
            scheduleDefaults.groupBreak
          ).map(match => {
            const previous = old.find(
              item =>
                !item.custom &&
                sourceKey(item.homeSource) === sourceKey(match.homeSource) &&
                sourceKey(item.awaySource) === sourceKey(match.awaySource)
            );
            return { ...match, id: previous?.id || match.id, eventId: previous?.eventId };
          })
        : old
            .filter(match => !match.custom)
            .map(match => ({
              ...match,
              removed: false,
              timeOverride: false,
              pitchOverride: false,
              durationOverride: false,
              venue: undefined
            }));
      setDefinition({
        ...next,
        matches: [...next.matches.filter(match => match.groupId !== activeBracket.id), ...generated],
        recreateGroupIds: Array.from(new Set([...(next.recreateGroupIds || []), activeBracket.id]))
      });
      setRecreatingRound(false);
      setPendingQualifierDefinition(undefined);
      return;
    }
    setDefinition({
      ...next,
      recreateMatchIds: Array.from(new Set([...(next.recreateMatchIds || []), ...ids])),
      recreateGroupIds: pendingQualifierDefinition
        ? Array.from(new Set([...(next.recreateGroupIds || []), activeBracket.id]))
        : next.recreateGroupIds
    });
    setRecreatingRound(false);
    setPendingQualifierDefinition(undefined);
  };
  const togglePanel = (name: string) => setOpenPanels(current => ({ ...current, [name]: !current[name] }));
  const updateGroupRoute = (groupId: string, patch: Partial<TournamentCompetitionDefinition['groupRoutes'][number]>) =>
    setDefinition(current => {
      const existing = current.groupRoutes.find(route => route.groupId === groupId) || {
        groupId,
        publicExplanation: '',
        destinations: []
      };
      return {
        ...current,
        groupRoutes: [
          ...current.groupRoutes.filter(route => route.groupId !== groupId),
          { ...existing, isAutomatic: false, ...patch }
        ]
      };
    });
  const updateDestination = (
    groupId: string,
    index: number,
    patch: { title?: string; places?: string; publicExplanation?: string }
  ) => {
    const route = tournamentStructurePublicRoutes(definition, naturalGroups).find(item => item.groupId === groupId);
    const destinations = [...(route?.destinations || [])];
    destinations[index] = { ...destinations[index], ...patch };
    updateGroupRoute(groupId, { destinations });
  };
  const tournamentVenueSource =
    (tournament.venue as any)?.placeData || (tournament.venue as any)?.postcodeData || tournament.venue;
  const tournamentVenueName = tournamentVenueSource
    ? getNameByPlaceOrPostcode(tournamentVenueSource)
    : 'Tournament location';
  const groupReview = runtime?.groupReview;
  const waitsForGroupResults = (match: TournamentStructureMatch) =>
    [match.homeSource, match.awaySource].some(source =>
      source.sourceType === 'GROUP_POSITION' && !runtime?.groups[source.sourceGroupId || '']?.isComplete
    );
  const criterionLabel = (criterion: string) =>
    TIE_BREAKERS.find(item => item.id === criterion)?.label || criterion.replace(/_/g, ' ').toLowerCase();
  const reviewMessage = manualRankingChanged
    ? 'The handoff order has changed. Confirm it again before recreating fixtures.'
    : groupReview?.status === 'CONFIRMED'
    ? 'Group qualification confirmed. You can still adjust the handoff order if play on the day requires it.'
    : groupReview?.status === 'STALE'
    ? 'A group result changed after qualification was confirmed. Review the standings again before any new group-fed fixtures are created.'
    : groupReview?.status === 'REQUIRES_DECISION'
    ? 'All group matches are complete, but one or more positions need an organiser decision.'
    : groupReview?.status === 'READY'
    ? 'All group matches are complete. Review the final standings and confirm the next-stage fixtures.'
    : 'Confirm each completed group separately. Fixtures are created once all of their source groups are confirmed.';
  const activeInitialGroup = structureGroups.find(
    group => group.id === activeBracketId && groupStageIds.has(group.stageId) && !qualifierIds.has(group.id)
  );
  const activeBracket =
    knockoutGroups.find(group => group.id === activeBracketId) ||
    naturalGroups.find(
      group =>
        knockoutGroups.some(item => item.id === group.id) &&
        definition.matches.some(match => match.groupId === group.id)
    ) ||
    knockoutGroups[0];
  const earlierSourceGroup = (sourceGroupId: string, destinationGroupId?: string) => {
    if (!destinationGroupId) return true;
    const order = definition.layout?.stages || [...structureStages].sort((a, b) => (a.order || 0) - (b.order || 0));
    const source = structureGroups.find(group => group.id === sourceGroupId),
      destination = structureGroups.find(group => group.id === destinationGroupId);
    return (
      !!source &&
      !!destination &&
      order.findIndex(stage => stage.id === source.stageId) < order.findIndex(stage => stage.id === destination.stageId)
    );
  };
  const canUseMatchSource = (source: TournamentMatchSource, destinationGroupId?: string, round?: string) => {
    const parent = definition.matches.find(match => match.id === source.sourceMatchId);
    if (parent && (parent.round === 'GROUP' || qualifierIds.has(parent.groupId))) return false;
    const parentRound = parent ? KNOCKOUT_ROUNDS.findIndex(item => item.id === matchRound(parent)) : -1;
    const nextRound = KNOCKOUT_ROUNDS.findIndex(item => item.id === round);
    return (
      !!parent &&
      (earlierSourceGroup(parent.groupId, destinationGroupId) ||
        (parent.groupId === destinationGroupId &&
          ((source.sourceType === 'MATCH_WINNER' &&
            parentRound >= 0 &&
            nextRound > parentRound &&
            parent.round !== 'PLAYOFF') ||
            (round === 'PLAYOFF' &&
              source.sourceType === 'MATCH_LOSER' &&
              parent.round !== 'FINAL' &&
              !isByeMatch(parent)))))
    );
  };
  const sourceIssues = definition.matches
    .filter(match => !match.removed)
    .reduce((issues, match) => {
      const invalid = [match.homeSource, match.awaySource].some(source =>
        source.sourceType === 'GROUP_POSITION'
          ? !sourceGroups.some(group => group.id === source.sourceGroupId) ||
            !earlierSourceGroup(source.sourceGroupId || '', match.groupId)
          : source.sourceType === 'TEAM'
          ? !source.teamId
          : source.sourceType === 'BYE'
          ? false
          : !canUseMatchSource(source, match.groupId, match.round)
      );
      return invalid
        ? [
            ...issues,
            `${dynamicMatchName(
              match
            )} has a missing entrant or a source outside an earlier stage. Its reference is retained.`
          ]
        : issues;
    }, [] as string[]);
  const activeBracketMatches = activeBracket
    ? definition.matches.filter(match => match.groupId === activeBracket.id)
    : [];
  const matchesByStartTime = [...definition.matches].sort(byStartTime);
  const bracketRounds =
    activeBracket && qualifierIds.has(activeBracket.id) ? [{ id: 'GROUP', label: 'Round robin' }] : KNOCKOUT_ROUNDS;
  const availableRounds = bracketRounds.filter(round =>
    activeBracketMatches.some(match => matchRound(match) === round.id)
  );
  const selectedRound =
    activeRound && bracketRounds.some(round => round.id === activeRound)
      ? activeRound
      : availableRounds[0]?.id || bracketRounds[0].id;
  const firstBracketRoundId =
    KNOCKOUT_ROUNDS.find(round => activeBracketMatches.some(match => !match.custom && matchRound(match) === round.id))
      ?.id || (activeBracketMatches.length ? selectedRound : 'SEMI_FINAL');
  const editingMatch = editingSlot ? definition.matches.find(match => match.id === editingSlot.matchId) : undefined;
  const customPool = editingMatch?.custom
    ? definition.qualifierGroups?.find(pool => pool.groupId === editingMatch.groupId)
    : undefined;
  const sourceDestinationGroupId =
    editingQualifierSlot !== undefined ? editingQualifierId || editingBracketId : editingMatch?.groupId;
  const editingLocationMatch = editingLocationMatchId
    ? definition.matches.find(match => match.id === editingLocationMatchId)
    : undefined;
  const matchContextTitle = (match?: TournamentStructureMatch) =>
    match
      ? `${knockoutGroups.find(group => group.id === match.groupId)?.name || 'Knockout'} · ${roundLabel(
          matchRound(match)
        )}`
      : 'Knockout match';

  const qualifierEntrantCount = qualifierSources.filter(source => source.sourceType !== 'BYE').length;
  const qualifierMatchCount = (qualifierEntrantCount * (qualifierEntrantCount - 1)) / 2;
  const qualifierFinish = new Date(
    +new Date(qualifierTiming.startTime) +
      (qualifierMatchCount * qualifierTiming.duration + Math.max(0, qualifierMatchCount - 1) * qualifierTiming.break) *
        60000
  );
  const liveSourceLabel = (source: TournamentMatchSource) => {
    if (source.label) return source.label;
    if (source.sourceType === 'TEAM')
      return source.teamId ? teamIdentity(source.teamId).displayName : 'Waiting for a team';
    if (source.sourceType === 'BYE') return 'Bye';
    if (source.sourceType === 'GROUP_POSITION')
      return `${ordinalLabel(source.sourcePosition || 1)} · ${liveGroups.find(
        group => group.id === source.sourceGroupId
      )?.name || 'Group'}`;
    const match = liveStructure?.matches.find(item => item.id === source.sourceMatchId);
    return `${source.sourceType === 'MATCH_WINNER' ? 'Winner' : 'Loser'} · ${match?.name || 'previous match'}`;
  };
  const liveKnockoutMatches = (liveStructure?.matches || []).filter(
    match => !match.removed && !livePools.has(match.groupId) && match.round !== 'GROUP'
  ).sort(byStartTime);
  const knockoutReadiness = liveKnockoutMatches.length > 0 && (
    <section data-needs-action="false">
      <header>
        <h3>Knockout fixture readiness</h3>
        <p>Fixtures are created when their participants are known.</p>
      </header>
      <div className="tswFixturesWrap">
        <table className="tswFixtures">
          <thead>
            <tr>
              <th>Competition</th>
              <th>Fixture</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {liveKnockoutMatches.map(match => {
              const item = runtime?.matches.find(row => row.matchId === match.id);
              const bye = isByeMatch(match);
              return (
                <tr key={match.id}>
                  <td>
                    {liveGroups.find(group => group.id === match.groupId)?.name}
                    <small>{roundLabel(match.round || 'FINAL')}</small>
                  </td>
                  <td>
                    <div className="tswFixtureSides">
                      {[match.homeSource, match.awaySource].map((source, index) => {
                        const id = index ? item?.awayTeamId : item?.homeTeamId;
                        const identity = id
                          ? teamIdentity(id)
                          : source.sourceType === 'TEAM' && source.teamId
                          ? teamIdentity(source.teamId)
                          : undefined;
                        return (
                          <div key={index}>
                            {identity && <SchoolMark name={identity.schoolName} pic={identity.schoolPic} />}
                            <span>{identity ? <>
                              {identity.schoolName}
                              <small>{identity.teamName}</small>
                              {source.sourceType !== 'TEAM' && <small>{(index ? match.awayOriginLabel : match.homeOriginLabel) || liveSourceLabel(source)}</small>}
                            </> : (index ? match.awayOriginLabel : match.homeOriginLabel) || liveSourceLabel(source)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </td>
                  <td>
                    <span className="tswBadge">
                      {bye
                        ? 'Bye · advances automatically'
                        : item?.eventId
                        ? 'Created'
                        : item?.status === 'READY' && !waitsForGroupResults(match)
                        ? 'Ready'
                        : 'Waiting for participants'}
                    </span>
                    {item?.sourceResultsChanged && (!item.eventId || !match.startTime || +new Date(match.startTime) > Date.now()) &&
                      <small>Source results changed since confirmation.</small>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
  const readyGroupIds = liveSourceGroups.filter(({ id }) => {
    const group = runtime?.groups[id];
    return group?.isComplete && !group.isConfirmed && !group.unresolved.length && group.resultHash;
  }).map(group => group.id);
  const canConfirmAll = hasPublished && !manualRankingChanged && readyGroupIds.length > 1;
  const reviewPanel = (
    <>
      {' '}
      {runtime && liveSourceGroups.length > 0 && (
        <section className={`tcsAdvancementReview is-${(groupReview?.status || 'NOT_READY').toLowerCase()}`}>
          <header>
            <div>
              <span className="tcsAdvancementReview__eyebrow">Group standings</span>
              <h3>
                {manualRankingChanged
                  ? 'Updated handoff needs confirmation'
                  : groupReview?.status === 'CONFIRMED'
                  ? 'Qualification confirmed'
                  : groupReview?.status === 'STALE'
                  ? 'Review required again'
                  : groupReview?.status === 'REQUIRES_DECISION'
                  ? 'Manual decision required'
                  : groupReview?.status === 'READY'
                  ? 'Ready for review'
                  : 'Groups in progress'}
              </h3>
              <p>{reviewMessage}</p>
            </div>
            {canConfirmAll && (
              <button
                type="button"
                className="tcsAdvancementReview__confirm"
                disabled={saving}
                onClick={() => confirmGroupAdvancement(readyGroupIds)}
              >
                Confirm all ready groups ({readyGroupIds.length})
              </button>
            )}
          </header>
          <div className="tcsAdvancementReview__groups">
            {liveSourceGroups.map(group => {
              const groupRuntime = runtime.groups[group.id];
              if (!groupRuntime) return null;
              const expanded = !!openPanels[`review-${group.id}`];
              const displayedStandings = displayedGroupStandings(group.id);
              const editingOrder = groupRuntime.isComplete && Boolean(openPanels[`order-${group.id}`]);
              const orderedTeamIds = displayedStandings.map(row => row.teamId);
              const hasManualOrder = displayedStandings.some(row => row.isManualOrder);
              const tieBreakApplied = displayedStandings.some(
                row => row.resolutionDetails && row.resolutionDetails.length
              );
              const needsDecision = groupRuntime.organiserStatus
                ? groupRuntime.organiserStatus === 'DECISION_NEEDED'
                : groupRuntime.isComplete && groupRuntime.unresolved.length > 0;
              const waitingForParticipants = !groupRuntime.createdMatches && livePools.has(group.id) &&
                liveStructure?.matches.some(match => match.groupId === group.id && !match.removed &&
                  !isByeMatch(match) && runtime.matches.some(item => item.matchId === match.id && item.status === 'WAITING'));
              return (
                <article
                  className={tieBreakApplied || hasManualOrder ? 'has-tiebreak' : ''}
                  key={group.id}
                  data-needs-action={groupRuntime.isComplete && (!groupRuntime.isConfirmed || !!manualOrders[group.id])}
                  data-review-group-id={group.id}
                >
                  <button type="button" onClick={() => togglePanel(`review-${group.id}`)}>
                    <span>
                      <strong>{group.name}</strong>
                      <small>
                        {groupRuntime.completedMatches || 0} of {groupRuntime.expectedMatches || 0} matches complete
                      </small>
                    </span>
                    <em
                      className={
                        needsDecision
                          ? 'is-warning'
                          : tieBreakApplied || hasManualOrder
                          ? 'is-tiebreak'
                          : groupRuntime.isComplete
                          ? 'is-ready'
                          : ''
                      }
                    >
                      {(groupRuntime.organiserStatus === 'CONFIRMED' || groupRuntime.isConfirmed) && !manualOrders[group.id]
                        ? 'Confirmed'
                        : needsDecision
                        ? 'Decision needed'
                        : hasManualOrder
                        ? 'Manual order pending'
                        : !groupRuntime.isComplete
                        ? waitingForParticipants ? 'Waiting for participants' : groupRuntime.organiserStatus === 'NOT_STARTED' ? 'Not started' : 'In progress'
                        : tieBreakApplied
                        ? 'Tie-break applied'
                        : 'Ready'}
                    </em>
                    <FontAwesomeIcon icon={expanded ? 'chevron-up' : 'chevron-down'} />
                  </button>
                  {expanded && (
                    <div>
                      {groupRuntime.resultsChanged && <p role="status">Results changed since confirmation.</p>}
                      {displayedStandings.map((row, index) => {
                        const identity = teamIdentity(row.teamId);
                        const hasTieBreak = Boolean(row.resolutionDetails && row.resolutionDetails.length);
                        const criteria = hasTieBreak
                          ? row.resolutionDetails.map(item => criterionLabel(item.criterion)).join(', then ')
                          : row.isResolved
                          ? 'Tournament points'
                          : 'Still level';
                        const resolvedBy = row.isManualOrder
                          ? `Manual order${hasTieBreak ? ` · previous criteria: ${criteria}` : ''}`
                          : criteria;
                        const tieBlock = !editingOrder && groupRuntime.tieBlocks?.find(block => block.teamIds.includes(row.teamId));
                        const endsTie = tieBlock && tieBlock.teamIds[tieBlock.teamIds.length - 1] === row.teamId;
                        return (
                          <React.Fragment key={row.teamId}>
                          <div
                            className={`tcsAdvancementReview__row ${
                              tieBlock ? 'tswTiedRow' : hasTieBreak || row.isManualOrder ? 'is-tiebreak' : ''
                            }`}
                            key={row.teamId}
                          >
                            <b>{row.position}{tieBlock ? '=' : ''}</b>
                            <SchoolMark name={identity.schoolName} pic={identity.schoolPic} />
                            <span>
                              <strong>{identity.displayName}</strong>
                            </span>
                            <dl>
                              <span>
                                <dt>P</dt>
                                <dd>{row.played || 0}</dd>
                              </span>
                              <span>
                                <dt>Pts</dt>
                                <dd>{row.points}</dd>
                              </span>
                              <span>
                                <dt>GD</dt>
                                <dd>{row.goalDifference}</dd>
                              </span>
                              <span>
                                <dt>GF</dt>
                                <dd>{row.goalsFor}</dd>
                              </span>
                              <span>
                                <dt>GA</dt>
                                <dd>{row.goalsAgainst}</dd>
                              </span>
                            </dl>
                            <em>
                              {!tieBlock && !row.isManualOrder && hasTieBreak && (row.isResolved || groupRuntime.isComplete) ? 'Tie-break · ' : ''}
                              {tieBlock ? '' : row.isResolved || groupRuntime.isComplete ? resolvedBy : ''}
                            </em>
                            {editingOrder && (
                              <div className="tswOrderControls">
                                {([-1, 1] as const).map(direction => {
                                  const label = `Move ${identity.displayName} ${direction < 0 ? 'up' : 'down'}`;
                                  return (
                                    <button type="button" key={direction} aria-label={label} title={label}
                                      disabled={saving || index + direction < 0 || index + direction >= orderedTeamIds.length}
                                      onClick={() => moveManualTeam(group.id, orderedTeamIds, index, direction)}>
                                      <FontAwesomeIcon icon={direction < 0 ? 'chevron-up' : 'chevron-down'} />
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          {endsTie && <p className="tswTieNote">
                            {tieBlock.teamIds.map(id => teamIdentity(id).displayName).join(' = ')} for {ordinalLabel(tieBlock.position)} ·{' '}
                            Level on {tieBlock.criteria.map(criterion => criterion === 'POINTS' ? 'Points' : criterionLabel(criterion)).join(', ')}
                          </p>}
                          </React.Fragment>
                        );
                      })}
                      {editingOrder ? (
                        <div className="tswOrderActions">
                          <button type="button" className="tswAdjustOrder" disabled={saving} onClick={() => {
                            setManualOrders(current => {
                              const next = { ...current };
                              delete next[group.id];
                              return next;
                            });
                            togglePanel(`order-${group.id}`);
                          }}>Cancel order changes</button>
                          <button type="button" className="tcsAdvancementReview__confirm" disabled={saving || !orderedTeamIds.length}
                            onClick={() => void confirmGroupAdvancement([group.id], orderedTeamIds)}>
                            Confirm {group.name} order
                          </button>
                        </div>
                      ) : <button
                        type="button"
                        className="tswAdjustOrder"
                        disabled={!groupRuntime.isComplete}
                        onClick={() => togglePanel(`order-${group.id}`)}
                      >
                        Adjust order
                      </button>}
                    </div>
                  )}
                  {!editingOrder && ((groupRuntime.organiserStatus !== 'CONFIRMED' && !groupRuntime.isConfirmed) || !!manualOrders[group.id]) && (
                    <div className="tcsAdvancementReview__groupActions">
                      <button
                        type="button"
                        className="tcsAdvancementReview__confirm"
                        disabled={
                          saving || !groupRuntime.isComplete || (groupRuntime.unresolved.length > 0 && !hasManualOrder)
                        }
                        onClick={() => confirmGroupAdvancement([group.id])}
                      >
                        Confirm {group.name} & create fixtures
                      </button>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      )}
    </>
  );
  const rankingPanel = (
    <section className="tcsEditor__compactSection">
      <header>
        <button type="button" className="tcsEditor__sectionToggle" onClick={() => togglePanel('ranking')}>
          <span>
            <h3>Ranking rules</h3>
            <p>
              Win {rankingPoints[0]} · Draw {rankingPoints[1]} · Loss {rankingPoints[3]} ·{' '}
              {definition.tieBreakers.length
                ? 'then ' +
                  definition.tieBreakers
                    .map(item => RANKING_SHORT_LABELS[item.criterion] || criterionLabel(item.criterion))
                    .join(' → ')
                : 'No tie-break criteria configured'}
              {!!definition.stageRules?.length && (
                <span className="tswBadge is-neutral"> · {definition.stageRules.length} stage overrides</span>
              )}
            </p>
          </span>
          <span>{openPanels.ranking ? 'Done' : 'Edit'}</span>
        </button>
      </header>
      {openPanels.ranking && (
        <div className="tcsEditor__sectionBody">
          <TournamentRankingEditor
            points={rankingPoints}
            criteria={definition.tieBreakers}
            onChange={(points, tieBreakers) =>
              setDefinition(current => ({ ...current, rankingPoints: points, tieBreakers }))
            }
          />
        </div>
      )}
    </section>
  );
  const displayPanel = (
    <>
      {' '}
      <section className="tcsEditor__compactSection">
        <header>
          <button className="tcsEditor__sectionToggle" type="button" onClick={() => togglePanel('display')}>
            <span>
              <h3>Knockout display order</h3>
              <p>{publicGroups.length} competitions</p>
            </span>
            <FontAwesomeIcon icon={openPanels.display ? 'chevron-up' : 'chevron-down'} />
          </button>
        </header>
        {openPanels.display && (
          <div className="tcsEditor__sectionBody">
            <div className="tcsEditor__displayOrder is-competitions-only">
              <div>
                <strong>Competitions</strong>
                {publicGroups.map((group, index) => (
                  <div key={group.id}>
                    <span>{group.name}</span>
                    <button
                      type="button"
                      disabled={!index}
                      onClick={() =>
                        moveDisplayItem(
                          'groupOrder',
                          publicGroups.map(item => item.id),
                          group.id,
                          -1
                        )
                      }
                    >
                      <FontAwesomeIcon icon="chevron-up" />
                    </button>
                    <button
                      type="button"
                      disabled={index === publicGroups.length - 1}
                      onClick={() =>
                        moveDisplayItem(
                          'groupOrder',
                          publicGroups.map(item => item.id),
                          group.id,
                          1
                        )
                      }
                    >
                      <FontAwesomeIcon icon="chevron-down" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  );
  const timingPanel = (
    <>
      {' '}
      <section className="tcsEditor__compactSection">
        <header>
          <button className="tcsEditor__sectionToggle" type="button" onClick={() => togglePanel('defaults')}>
            <span>
              <h3>Match timing defaults</h3>
              <p>
                Group {scheduleDefaults.groupDuration} min + {scheduleDefaults.groupBreak} min break · Knockout{' '}
                {scheduleDefaults.knockoutDuration} min + {scheduleDefaults.knockoutBreak} min break
              </p>
            </span>
            <FontAwesomeIcon icon={openPanels.defaults ? 'chevron-up' : 'chevron-down'} />
          </button>
        </header>
        {openPanels.defaults && (
          <div className="tcsEditor__sectionBody tcsEditor__defaults">
            <div>
              <strong>Group matches</strong>
              <label>
                <span>Duration</span>
                <input
                  type="number"
                  min="1"
                  max="180"
                  value={scheduleDefaults.groupDuration}
                  onChange={e =>
                    setDefinition(current => ({
                      ...current,
                      scheduleDefaults: {
                        ...(current.scheduleDefaults || scheduleDefaults),
                        groupDuration: Number(e.target.value)
                      }
                    }))
                  }
                />
                <em>min</em>
              </label>
              <label>
                <span>Break</span>
                <input
                  type="number"
                  min="0"
                  max="180"
                  value={scheduleDefaults.groupBreak}
                  onChange={e =>
                    setDefinition(current => ({
                      ...current,
                      scheduleDefaults: {
                        ...(current.scheduleDefaults || scheduleDefaults),
                        groupBreak: Number(e.target.value)
                      }
                    }))
                  }
                />
                <em>min</em>
              </label>
            </div>
            <div>
              <strong>Knockout matches</strong>
              <label>
                <span>Duration</span>
                <input
                  type="number"
                  min="1"
                  max="180"
                  value={scheduleDefaults.knockoutDuration}
                  onChange={e =>
                    setDefinition(current => ({
                      ...current,
                      scheduleDefaults: {
                        ...(current.scheduleDefaults || scheduleDefaults),
                        knockoutDuration: Number(e.target.value)
                      }
                    }))
                  }
                />
                <em>min</em>
              </label>
              <label>
                <span>Break</span>
                <input
                  type="number"
                  min="0"
                  max="180"
                  value={scheduleDefaults.knockoutBreak}
                  onChange={e =>
                    setDefinition(current => ({
                      ...current,
                      scheduleDefaults: {
                        ...(current.scheduleDefaults || scheduleDefaults),
                        knockoutBreak: Number(e.target.value)
                      }
                    }))
                  }
                />
                <em>min</em>
              </label>
            </div>
          </div>
        )}
      </section>
    </>
  );
  const bulkPitchControl = (groupIds: string[]) => (
    <label className="tswBulkPitch">
      Set pitch for all matches
      <select
        aria-label="Set pitch for all matches"
        value=""
        disabled={!runtime || !groupIds.length || !tournament.pitches?.length}
        onChange={event => {
          const pitchId = event.target.value;
          if (!pitchId) return;
          setDefinition(current => {
            const layout = current.layout || initialStructureLayout(tournament, runtime);
            return {
              ...current,
              layout: {
                ...layout,
                groups: layout.groups.map(group =>
                  groupIds.includes(group.id) ? { ...group, pitchId, pitchMode: 'SET' } : group
                )
              },
              matches: current.matches.map(match =>
                groupIds.includes(match.groupId) && !match.removed ? { ...match, pitchId, pitchOverride: false } : match
              )
            };
          });
        }}
      >
        <option value="">Choose a pitch</option>
        {tournament.pitches?.map(pitch => (
          <option key={pitch.id} value={pitch.id}>
            {pitch.name}
          </option>
        ))}
      </select>
      <small>Applies to planned fixtures. You can change any match individually.</small>
    </label>
  );
  const builderPanel = (
    <>
      {' '}
      <div className={`tcsBuilder ${sourcesCollapsed ? 'is-sources-collapsed' : ''}`}>
        <aside className={`tcsPalette ${sourcesCollapsed ? 'is-collapsed' : ''}`}>
          <button
            className="tcsPalette__collapse"
            type="button"
            aria-label={sourcesCollapsed ? 'Show group places' : 'Hide group places'}
            title={sourcesCollapsed ? 'Show group places' : 'Hide group places'}
            onClick={() => setSourcesCollapsed(value => !value)}
          >
            <FontAwesomeIcon icon={sourcesCollapsed ? 'chevron-right' : 'chevron-left'} />
          </button>
          {!sourcesCollapsed && (
            <>
              <header>
                <span>Sources</span>
                <h3>Sources</h3>
                <p>Drag a source into an entrant slot, or select a slot to choose.</p>
              </header>
              <input
                type="search"
                aria-label="Search participant sources"
                placeholder="Find a source"
                value={sourceQuery}
                onChange={event => setSourceQuery(event.target.value)}
              />
              {(['Groups', 'Qualifiers'] as const).map(section => {
                const groups = sourceGroups.filter(
                  group => (section === 'Qualifiers') === !groupStageIds.has(group.stageId)
                );
                if (
                  !groups.some(
                    group =>
                      earlierSourceGroup(group.id, activeBracket?.id) &&
                      group.name.toLowerCase().includes(sourceQuery.toLowerCase())
                  )
                )
                  return null;
                return (
                  <details key={section} className="tswSourceList" open={sourceQuery ? true : undefined}>
                    <summary>{section}</summary>
                    <div>
                      {groups
                        .filter(
                          group =>
                            earlierSourceGroup(group.id, activeBracket?.id) &&
                            (!sourceQuery || group.name.toLowerCase().includes(sourceQuery.toLowerCase()))
                        )
                        .map(group => {
                          const positions = positionsForGroup(group.id);
                          return (
                            <div key={group.id} className="tswSourceGroup">
                              <strong>
                                {group.name}
                                <small>{positions.length} places</small>
                              </strong>
                              <div>
                                {positions.map(position =>
                                  sourceChip(
                                    { sourceType: 'GROUP_POSITION', sourceGroupId: group.id, sourcePosition: position },
                                    `${ordinalLabel(position)} place`
                                  )
                                )}
                              </div>
                              <button
                                hidden={qualifierIds.has(group.id)}
                                className="tcsPalette__more"
                                type="button"
                                onClick={() =>
                                  setPositionOverrides(current => ({ ...current, [group.id]: positions.length + 1 }))
                                }
                              >
                                + Add {ordinalLabel(positions.length + 1)} place
                              </button>
                            </div>
                          );
                        })}
                    </div>
                  </details>
                );
              })}
              <details className="tswSourceList" open={sourceQuery ? true : undefined}>
                <summary>Teams</summary>
                <div>
                  {runtime?.teams
                    .filter(
                      team =>
                        !sourceQuery ||
                        teamIdentity(team.id)
                          .displayName.toLowerCase()
                          .includes(sourceQuery.toLowerCase())
                    )
                    .map(team => {
                      const identity = teamIdentity(team.id);
                      return sourceChip(
                        { sourceType: 'TEAM', teamId: team.id },
                        <>
                          <SchoolMark name={identity.schoolName} pic={identity.schoolPic} />
                          <span className="tswSourceTeamName">
                            <strong>{identity.schoolName}</strong>
                            {identity.teamName !== identity.schoolName && <small>{identity.teamName}</small>}
                          </span>
                        </>
                      );
                    })}
                </div>
              </details>
              <details className="tswSourceList" open={sourceQuery ? true : undefined}>
                <summary>Earlier knockout matches</summary>
                <div>
                  {matchesByStartTime
                    .filter(
                      match =>
                        match.round !== 'GROUP' &&
                        !qualifierIds.has(match.groupId) &&
                        earlierSourceGroup(match.groupId, activeBracket?.id) &&
                        (!sourceQuery ||
                          dynamicMatchName(match)
                            .toLowerCase()
                            .includes(sourceQuery.toLowerCase()))
                    )
                    .map(match => (
                      <div key={match.id}>
                        <strong>{dynamicMatchName(match)}</strong>
                        {sourceChip({ sourceType: 'MATCH_WINNER', sourceMatchId: match.id }, 'Winner')}
                        {!isByeMatch(match) &&
                          sourceChip({ sourceType: 'MATCH_LOSER', sourceMatchId: match.id }, 'Loser')}
                      </div>
                    ))}
                </div>
              </details>
              {(!sourceQuery || 'bye'.includes(sourceQuery.toLowerCase())) &&
                sourceChip({ sourceType: 'BYE' }, 'Bye · no opponent')}
            </>
          )}
        </aside>
        <div className="tcsBracket">
          <header>
            <div>
              <span className="tswEyebrow">
                {qualifierIds.has(activeBracket?.id || '') ? 'Round robin' : 'Knockout competition'}
              </span>
              <h2>{activeBracket?.name || 'Competition'}</h2>
              <p>
                {activeBracket ? stageById.get(activeBracket.stageId)?.name : 'Choose a competition in the stage tree.'}
              </p>
            </div>
            {activeBracket && (
              <div className="tswCompetitionSettings">
                <label>
                  Name
                  <input
                    aria-label="Competition name"
                    value={activeBracket.name}
                    onChange={event => {
                      const name = event.target.value;
                      updateLayout(current => ({
                        ...current,
                        groups: current.groups.map(group =>
                          group.id === activeBracket.id ? { ...group, name } : group
                        )
                      }));
                    }}
                  />
                </label>
                {!qualifierIds.has(activeBracket.id) && (
                  <label>
                    First round
                    <select
                      aria-label="First knockout round"
                      disabled={!runtime}
                      value={firstBracketRoundId === 'ROUND_OF_8' ? 'QUARTER_FINAL' : firstBracketRoundId}
                      onChange={event => {
                        const firstRound = event.target.value;
                        const matches = resizeKnockoutFixtures(
                          definition.matches,
                          activeBracket.id,
                          activeBracket.name,
                          firstRound,
                          stageById.get(activeBracket.stageId)?.startTime || tournament.startTime,
                          scheduleDefaults.knockoutDuration,
                          scheduleDefaults.knockoutBreak
                        );
                        setDefinition(current => ({
                          ...current,
                          layout: current.layout || initialStructureLayout(tournament, runtime),
                          matches: [...current.matches.filter(match => match.groupId !== activeBracket.id), ...matches]
                        }));
                        setActiveRound(firstRound);
                      }}
                    >
                      {BRACKET_ROUNDS.map(round => (
                        <option key={round.id} value={round.id}>
                          {round.label}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <label>
                  Default pitch
                  <select
                    value={activeBracket.pitchMode === 'NONE' ? '__none' : activeBracket.pitchId || ''}
                    onChange={event => {
                      const value = event.target.value;
                      const pitchId = value === '__none' ? undefined : value || undefined;
                      updateLayout(current => ({
                        ...current,
                        groups: current.groups.map(group =>
                          group.id === activeBracket.id
                            ? { ...group, pitchId, pitchMode: value === '__none' ? 'NONE' : value ? 'SET' : 'AUTO' }
                            : group
                        )
                      }));
                    }}
                  >
                    <option value="">Auto</option>
                    <option value="__none">Not set</option>
                    {tournament.pitches?.map(pitch => (
                      <option key={pitch.id} value={pitch.id}>
                        {pitch.name}
                      </option>
                    ))}
                  </select>
                </label>
                {bulkPitchControl([activeBracket.id])}
              </div>
            )}
          </header>
          {!knockoutStages.length && (
            <div className="tcsBracket__notice">
              Add a Qualifier, Playoff or Final stage to the tournament layout before creating knockout competitions.
            </div>
          )}
          {!activeBracket && knockoutStages.length > 0 && (
            <div className="tcsBracket__empty">
              <strong>No knockout competitions yet</strong>
              <span>
                Add Cup, Plate or another competition above. You will then be able to create quarter-finals,
                semi-finals, play-offs and finals inside it.
              </span>
            </div>
          )}
          {activeBracket && (
            <div className="tcsQualifierActions">
              {!activeBracketMatches.length && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingBracketId(activeBracket.id);
                    setBracketFirstRound('SEMI_FINAL');
                    setQualifierSources(Array.from({ length: 4 }, () => ({ sourceType: 'TEAM' })));
                  }}
                >
                  Generate knockout bracket
                </button>
              )}
              <button
                type="button"
                disabled={!qualifierIds.has(activeBracket.id) && activeBracketMatches.length > 0}
                onClick={() => configureQualifier(activeBracket.id)}
              >
                {qualifierIds.has(activeBracket.id) ? 'Edit qualifier entrants' : 'Configure as qualifier group'}
              </button>
              <p>
                {qualifierIds.has(activeBracket.id)
                  ? 'Entrants play each other once. Use this group’s finishing places as sources for finals and play-offs.'
                  : 'Select finishing places from earlier groups to populate this group.'}
              </p>
              {qualifierLocked(activeBracket.id) && (
                <p>
                  Changing entrants or timing requires confirmation before existing fixtures and results are replaced.
                </p>
              )}
            </div>
          )}
          {activeBracket && (
            <div className="tswEntrants">
              <header>
                <h3>Entrants</h3>
                <p>Choose a slot to change its participant source.</p>
              </header>
              <div>
                {(
                  definition.qualifierGroups?.find(pool => pool.groupId === activeBracket.id)?.sources ||
                  activeBracketMatches
                    .filter(match => !match.custom && matchRound(match) === firstBracketRoundId)
                    .reduce(
                      (all, match) => all.concat([match.homeSource, match.awaySource]),
                      [] as TournamentMatchSource[]
                    )
                ).map((source, index) => {
                  const pool = definition.qualifierGroups?.find(item => item.groupId === activeBracket.id);
                  const firstRoundMatches = activeBracketMatches.filter(
                    item => !item.custom && matchRound(item) === firstBracketRoundId
                  );
                  const match = pool
                    ? activeBracketMatches.find(
                        item =>
                          sourceKey(item.homeSource) === sourceKey(source) ||
                          sourceKey(item.awaySource) === sourceKey(source)
                      )
                    : firstRoundMatches[Math.floor(index / 2)];
                  const identity = sourceTeamIdentity(source);
                  const slot: 'homeSource' | 'awaySource' = pool
                    ? sourceKey(match?.homeSource || source) === sourceKey(source)
                      ? 'homeSource'
                      : 'awaySource'
                    : index % 2
                    ? 'awaySource'
                    : 'homeSource';
                  return (
                    <button
                      type="button"
                      key={index}
                      disabled={!match}
                      onDragOver={event => event.preventDefault()}
                      onDrop={event => {
                        event.preventDefault();
                        if (!match) return;
                        try {
                          const value = JSON.parse(event.dataTransfer.getData('application/x-tournament-source'));
                          if (
                            !['GROUP_POSITION', 'MATCH_WINNER', 'MATCH_LOSER', 'TEAM', 'BYE'].includes(value.sourceType)
                          )
                            throw new Error();
                          setSlot(match.id!, slot, value);
                        } catch (error) {
                          setError('Drop a participant source from the Sources panel.');
                        }
                      }}
                      onClick={() => {
                        if (
                          pool &&
                          source.sourceType === 'BYE' &&
                          pool.sources.filter(item => item.sourceType === 'BYE').length > 1
                        ) {
                          configureQualifier(activeBracket.id);
                          setEditingQualifierSlot(index);
                          return;
                        }
                        if (match && selectedSource) {
                          setSlot(match.id!, slot, selectedSource);
                          return;
                        }
                        if (match)
                          setEditingSlot({
                            matchId: match.id!,
                            slot
                          });
                      }}
                    >
                      <b>{index + 1}</b>
                      {identity && <SchoolMark name={identity.schoolName} pic={identity.schoolPic} />}
                      <span>
                        {sourceLabel(source)}
                        {identity && source.sourceType !== 'TEAM' && (
                          <small>
                            {dirty ? 'Current: ' : ''}
                            {identity.displayName}
                          </small>
                        )}
                      </span>
                      <small>Change</small>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {activeBracket && !qualifierIds.has(activeBracket.id) && activeBracketMatches.length > 0 && (
            <div className="tswSegment">
              <button type="button" aria-pressed={!bracketView} onClick={() => setBracketView(false)}>
                Fixtures
              </button>
              <button type="button" aria-pressed={bracketView} onClick={() => setBracketView(true)}>
                Bracket
              </button>
            </div>
          )}
          {bracketView && activeBracket && !qualifierIds.has(activeBracket.id) && (
            <TournamentKnockoutBracket
              matches={[...activeBracketMatches].sort(byStartTime).map(match => ({...match, name: fixtureDisplayName(match)}))}
              label={sourceLabel}
              team={source => {
                if (source.sourceType !== 'TEAM' || !source.teamId) return undefined;
                const school = teamIdentity(source.teamId);
                return { name: school.schoolName, pic: school.schoolPic };
              }}
              edit={openMatchEditor}
            />
          )}
          {activeBracket && (
            <nav className="tcsBracket__rounds">
              {bracketRounds.map(round => {
                const count = activeBracketMatches.filter(match => matchRound(match) === round.id).length;
                return (
                  <button
                    type="button"
                    className={selectedRound === round.id ? 'is-active' : ''}
                    onClick={() => setActiveRound(round.id)}
                    key={round.id}
                  >
                    {round.label}
                    {count > 0 && <small>{count}</small>}
                  </button>
                );
              })}
            </nav>
          )}
          <div className="tcsBracket__columns">
            {activeBracket
              ? [activeBracket].map(stageGroup => (
                  <section
                    key={`${stageGroup.id}-${selectedRound}`}
                    className="is-active-branch"
                    onDragOver={event => event.preventDefault()}
                    onDrop={event => {
                      event.preventDefault();
                      const moved = event.dataTransfer.getData('application/x-structure-match');
                      if (
                        moved &&
                        !qualifierIds.has(stageGroup.id) &&
                        !qualifierIds.has(definition.matches.find(item => item.id === moved)?.groupId || '')
                      )
                        patchMatch(moved, { groupId: stageGroup.id, round: selectedRound });
                    }}
                  >
                    <header>
                      <span>
                        {stageGroup.name} · {roundLabel(selectedRound)}
                      </span>
                      <div className="tcsBracket__roundActions">
                        {definition.matches.some(
                          match => match.groupId === stageGroup.id && matchRound(match) === selectedRound
                        ) && (
                          <button
                            type="button"
                            className="is-recreate"
                            disabled={saving}
                            onClick={() => setRecreatingRound(true)}
                          >
                            Recreate fixtures
                          </button>
                        )}
                        <button type="button" onClick={() => addMatch(stageGroup.id, selectedRound)}>
                          + Add match
                        </button>
                      </div>
                    </header>
                    <div className="tswFixturesWrap">
                      <table className="tswFixtures">
                        <thead>
                          <tr>
                            <th>Time</th>
                            <th>Fixture</th>
                            <th>Pitch</th>
                            <th>Status</th>
                            <th>
                              <span className="sr-only">Actions</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {matchesByStartTime
                            .filter(match => match.groupId === stageGroup.id && matchRound(match) === selectedRound)
                            .map(match => {
                              const resolved = runtime?.matches.find(item => item.matchId === match.id);
                              return (
                                <tr
                                  key={match.id}
                                  className={match.removed ? 'is-removed' : ''}
                                  data-structure-match-id={match.id}
                                >
                                  <td>
                                    {match.startTime
                                      ? new Date(match.startTime).toLocaleTimeString('en-GB', {
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })
                                      : 'Not set'}
                                    <small>{matchDuration(match)} min</small>
                                  </td>
                                  <td>
                                    <small>{fixtureDisplayName(match)}</small>
                                    <div className="tswFixtureSides">
                                      {(['homeSource', 'awaySource'] as const).map((slot, index) => {
                                        const source = match[slot];
                                        const team =
                                          source.sourceType === 'TEAM'
                                            ? sourceTeamIdentity(source)
                                            : resolvedTeamIdentity(match.id, index ? 'away' : 'home');
                                        return (
                                          <div key={slot}>
                                            {team && <SchoolMark name={team.schoolName} pic={team.schoolPic} />}
                                            <span>
                                              {team ? <>
                                                {team.schoolName}
                                                <small>{team.teamName}</small>
                                                {source.sourceType !== 'TEAM' && <small>{(index ? match.awayOriginLabel : match.homeOriginLabel) || sourceLabel(source)}</small>}
                                              </> : (index ? match.awayOriginLabel : match.homeOriginLabel) || sourceLabel(source)}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </td>
                                  <td>
                                    {tournament.pitches?.find(pitch => pitch.id === match.pitchId)?.name || 'Not set'}
                                  </td>
                                  <td>
                                    <span className={isByeMatch(match) ? 'tswBadge' : 'tswBadge is-neutral'}>
                                      {match.removed
                                        ? 'Removed'
                                        : isByeMatch(match)
                                        ? 'Bye'
                                        : resolved?.eventId
                                        ? 'Created'
                                        : resolved?.status === 'READY' && !waitsForGroupResults(match)
                                        ? 'Ready'
                                        : 'Waiting for participants'}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="tswFixtureActions">
                                      <button type="button" onClick={() => openMatchEditor(match.id!)}>
                                        Edit
                                      </button>
                                      <button
                                        type="button"
                                        aria-label={match.removed ? 'Restore match' : 'Remove match'}
                                        disabled={
                                          saving ||
                                          (qualifierIds.has(match.groupId) &&
                                            !isByeMatch(match) &&
                                            !match.generated &&
                                            !match.custom)
                                        }
                                        onClick={() =>
                                          match.removed ? patchMatch(match.id!, { removed: false }) : removeMatch(match)
                                        }
                                      >
                                        {match.removed ? 'Restore' : '×'}
                                      </button>
                                    </div>
                                    {blockedRemovalId === match.id && (
                                      <p role="alert">
                                        This match is used by a later match. Change its winner or loser source before
                                        removing it.
                                      </p>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                    {matchesByStartTime
                      .filter(match => match.groupId === stageGroup.id && matchRound(match) === selectedRound)
                      .map(match => {
                        const expanded = !!expandedMatches[match.id!];
                        const runtimeMatch = runtime?.matches.find(item => item.matchId === match.id);
                        const readyForReview =
                          runtimeMatch?.status === 'READY' &&
                          !definition.qualifierGroups?.length &&
                          groupReview?.status !== 'CONFIRMED';
                        return (
                          <SimpleModal
                            isOpen={expanded}
                            title={
                              <>
                                {fixtureDisplayName(match)}
                                <small className="tswDialogSubtitle">{stageById.get(stageGroup.stageId)?.name}</small>
                              </>
                            }
                            showFooter={false}
                            customClass="tcsChoiceModal tswMatchModal"
                            dialogStyle={{ maxWidth: 720 }}
                            key={match.id}
                            onCloseClick={() => closeMatchEditor(true)}
                          >
                            <article
                              data-match-editor-id={match.id}
                              tabIndex={-1}
                              className={`tcsMatch ${expanded ? 'is-expanded' : 'is-collapsed'} ${
                                readyForReview ? 'is-ready-for-review' : ''
                              } ${recentlyAddedMatchId === match.id ? 'is-newly-added' : ''}`}
                              key={match.id}
                              draggable
                              onDragStart={event =>
                                event.dataTransfer.setData('application/x-structure-match', match.id || '')
                              }
                            >
                              {blockedRemovalId === match.id && (
                                <p role="alert" className="tcsEditor__error">
                                  This match is used by a later match. Change its winner or loser source before removing
                                  it.
                                </p>
                              )}
                              {expanded && (
                                <div className="tcsMatch__details">
                                  {match.custom && (
                                    <label className="tswMatchLabel">
                                      Match label
                                      <input
                                        aria-label="Match label"
                                        value={match.name}
                                        onChange={event => patchMatch(match.id!, { name: event.target.value })}
                                      />
                                    </label>
                                  )}
                                  {match.generated && !match.custom && match.round !== 'GROUP' && (
                                    <p>
                                      First-round entrants are edited in the competition slots. Later rounds use
                                      previous winners automatically.
                                    </p>
                                  )}
                                  <div
                                    className={`tcsMatch__schedule ${isSingleDay ? 'is-single-day' : 'is-multi-day'}`}
                                  >
                                    {!isSingleDay && (
                                      <DatePicker
                                        label="Date"
                                        value={match.startTime ? new Date(match.startTime) : null}
                                        min={tournament.startTime}
                                        max={tournament.endTime}
                                        onChange={value => setMatchDate(match, value as Date | null)}
                                        clearable={false}
                                        size="sm"
                                      />
                                    )}
                                    <TimePicker
                                      label="Start time"
                                      value={match.startTime ? new Date(match.startTime) : null}
                                      onChange={value => setMatchTime(match, value)}
                                      minuteStep={5}
                                      clearable={false}
                                      size="sm"
                                      align="left"
                                    />
                                    <label className="tcsMatch__compactField">
                                      <span>Duration</span>
                                      <span className="tcsMatch__duration">
                                        <input
                                          type="number"
                                          min="1"
                                          max="180"
                                          value={matchDuration(match)}
                                          onChange={e => setMatchDuration(match, Number(e.target.value))}
                                        />
                                        <em>min</em>
                                      </span>
                                    </label>
                                  </div>
                                  <div className="tcsMatch__venue">
                                    <span>
                                      <small>Location</small>
                                      <strong>
                                        {match.venue?.name || match.venue?.postcode || tournamentVenueName}
                                      </strong>
                                    </span>
                                    <button type="button" onClick={() => setEditingLocationMatchId(match.id)}>
                                      Change
                                    </button>
                                    <label className="tcsMatch__pitch">
                                      <small>Pitch</small>
                                      <select
                                        value={match.pitchId || ''}
                                        onChange={e =>
                                          patchMatch(match.id!, {
                                            pitchId: e.target.value || undefined,
                                            pitchOverride: true
                                          })
                                        }
                                      >
                                        <option value="">Not set</option>
                                        {(tournament.pitches || []).map(pitch => (
                                          <option key={pitch.id} value={pitch.id}>
                                            {pitch.name}
                                          </option>
                                        ))}
                                      </select>
                                    </label>
                                  </div>
                                </div>
                              )}
                              <div className="tcsMatch__participants">
                                {(['homeSource', 'awaySource'] as const).map((slot, index) => {
                                  const resolved = resolvedTeamIdentity(match.id, index ? 'away' : 'home');
                                  return (
                                    <div className="tcsMatch__slotWrap" key={slot}>
                                      <button
                                        type="button"
                                        className="tcsMatch__slot"
                                        disabled={match.generated && !match.custom && match.round !== 'GROUP'}
                                        onClick={event => {
                                          event.stopPropagation();
                                          if (selectedSource) setSlot(match.id!, slot, selectedSource);
                                          else setEditingSlot({ matchId: match.id!, slot });
                                        }}
                                        onDragOver={event => {
                                          event.preventDefault();
                                          event.stopPropagation();
                                        }}
                                        onDrop={event => {
                                          event.preventDefault();
                                          event.stopPropagation();
                                          const raw = event.dataTransfer.getData('application/x-tournament-source');
                                          if (raw) setSlot(match.id!, slot, JSON.parse(raw));
                                        }}
                                      >
                                        <small>
                                          {index ? 'Second team' : 'First team'}
                                          <span>Change</span>
                                        </small>
                                        <strong>{sourceLabel(match[slot])}</strong>
                                        {resolved && (
                                          <em className="tcsMatch__resolvedTeam">
                                            <SchoolMark name={resolved.schoolName} pic={resolved.schoolPic} />
                                            <span>
                                              <b>{resolved.schoolName}</b>
                                              <small>{resolved.teamName}</small>
                                            </span>
                                          </em>
                                        )}
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="tcsMatch__outputs">
                                <span>{isByeMatch(match) ? 'Advances automatically:' : 'Drag onward:'}</span>
                                {sourceChip({ sourceType: 'MATCH_WINNER', sourceMatchId: match.id }, 'Winner')}
                                {!isByeMatch(match) &&
                                  sourceChip({ sourceType: 'MATCH_LOSER', sourceMatchId: match.id }, 'Loser')}
                                <button
                                  type="button"
                                  className="tcsMatch__fixtureAction"
                                  disabled={
                                    saving || runtimeMatch?.status === 'WAITING' || runtimeMatch?.status === 'BYE' ||
                                    (!runtimeMatch?.eventId && !match.eventId && waitsForGroupResults(match))
                                  }
                                  onClick={() => {
                                    closeMatchEditor();
                                    runtimeMatch?.eventId || match.eventId
                                      ? setReplacingMatchId(match.id)
                                      : void materializeMatch(match);
                                  }}
                                >
                                  {runtimeMatch?.eventId || match.eventId ? 'Replace fixture' : 'Create fixture'}
                                </button>
                              </div>
                              <footer className="tswMatchFooter">
                                <button
                                  type="button"
                                  className="is-danger"
                                  disabled={
                                    saving ||
                                    (qualifierIds.has(match.groupId) &&
                                      !isByeMatch(match) &&
                                      !match.generated &&
                                      !match.custom)
                                  }
                                  onClick={() => {
                                    if (removeMatch(match)) closeMatchEditor();
                                  }}
                                >
                                  Remove match
                                </button>
                                <button type="button" onClick={() => closeMatchEditor(true)}>
                                  Cancel
                                </button>
                                <button type="button" className="is-primary" onClick={() => closeMatchEditor()}>
                                  Save match
                                </button>
                              </footer>
                            </article>
                          </SimpleModal>
                        );
                      })}
                  </section>
                ))
              : null}
          </div>
        </div>
      </div>
    </>
  );
  const publicPanel = (
    <>
      {' '}
      <section className="tcsPublicContent">
        <header>
          <div>
            <h3>Public progression information</h3>
            <p>
              Explain where teams go next and how level teams are separated. Only completed fields appear on the public
              site.
            </p>
          </div>
        </header>
        <div className="tswRulesSummary">
          <strong>Ranking summary</strong>
          <p>
            {rankingPoints[0]} points for a win; {rankingPoints[1]} for a draw with goals; {rankingPoints[2]} for a 0–0
            draw; {rankingPoints[3]} for a loss.{' '}
            {definition.tieBreakers.map(item => criterionLabel(item.criterion)).join(' → ')}
          </p>
          {definition.stageRules?.map(rule => (
            <p key={rule.stageId}>
              <strong>{stageById.get(rule.stageId)?.name}:</strong> {rule.points[0]} win / {rule.points[1]} draw with
              goals / {rule.points[2]} draw 0–0 / {rule.points[3]} loss.{' '}
              {rule.tieBreakers.map(item => criterionLabel(item.criterion)).join(' → ')}
            </p>
          ))}
        </div>
        <div className="tcsPublicContent__rules">
          <label>
            <span>Group rules</span>
            <small>
              We already show the configured ranking criteria. Use this field for a clear public explanation, including
              points and any organiser decision.
            </small>
            <textarea
              placeholder="For example: Three points for a win, one for a draw. Level teams are separated on goal difference…"
              value={definition.groupRulesPublicSummary || ''}
              onChange={event =>
                setDefinition(current => ({ ...current, groupRulesPublicSummary: event.target.value }))
              }
            />
          </label>
        </div>
        <div className="tcsEditor__summaries">
          {naturalGroups.map(group => {
            const route = tournamentStructurePublicRoutes(definition, naturalGroups).find(
              item => item.groupId === group.id
            );
            const stage = stageById.get(group.stageId);
            const isGroup = groupStageIds.has(group.stageId);
            return (
              <article className="tcsRouteEditor" key={group.id}>
                <header>
                  <span>
                    {isGroup && <small>{stage?.name || 'Group stage'}</small>}
                    <strong>{group.name}</strong>
                    <small>{route?.isAutomatic ? 'Auto' : 'Custom'}</small>
                  </span>
                  <button type="button" onClick={() => updateGroupRoute(group.id, { isAutomatic: true })}>
                    Use automatic progression
                  </button>
                  {isGroup && (
                    <button
                      type="button"
                      onClick={() =>
                        updateGroupRoute(group.id, {
                          destinations: [
                            ...(route?.destinations || []),
                            { title: '', places: '', publicExplanation: '' }
                          ]
                        })
                      }
                    >
                      + Add destination
                    </button>
                  )}
                </header>
                {isGroup ? (
                  <>
                    {(route?.destinations || []).map((destination, index) => (
                      <div className="tcsRouteEditor__destination" key={index}>
                        <label>
                          <span>Destination</span>
                          <input
                            placeholder="Cup, Plate, Shield…"
                            value={destination.title}
                            onChange={event => updateDestination(group.id, index, { title: event.target.value })}
                          />
                        </label>
                        <label>
                          <span>Places</span>
                          <input
                            placeholder="1st–2nd"
                            value={destination.places || ''}
                            onChange={event => updateDestination(group.id, index, { places: event.target.value })}
                          />
                        </label>
                        <button
                          type="button"
                          aria-label="Remove destination"
                          onClick={() =>
                            updateGroupRoute(group.id, {
                              destinations: (route?.destinations || []).filter((_, itemIndex) => itemIndex !== index)
                            })
                          }
                        >
                          ×
                        </button>
                        <label className="is-summary">
                          <span>Public explanation</span>
                          <textarea
                            placeholder="Top two go into the eight-team Cup…"
                            value={destination.publicExplanation}
                            onChange={event =>
                              updateDestination(group.id, index, { publicExplanation: event.target.value })
                            }
                          />
                        </label>
                      </div>
                    ))}
                    {!(route?.destinations || []).length && (
                      <div className="tcsRouteEditor__empty">No public destinations added.</div>
                    )}
                  </>
                ) : (
                  <label className="tcsRouteEditor__explanation">
                    <span>Public explanation</span>
                    <textarea
                      placeholder="Explain where teams go from this section"
                      value={route?.publicExplanation || ''}
                      onChange={event => updateGroupRoute(group.id, { publicExplanation: event.target.value })}
                    />
                  </label>
                )}
              </article>
            );
          })}
        </div>
      </section>
    </>
  );
  const editorOverlays = (
    <>
      {' '}
      <SimpleModal
        isOpen={!!editingLocationMatch}
        title={matchContextTitle(editingLocationMatch)}
        showFooter={false}
        customClass="tcsChoiceModal"
        dialogStyle={{ maxWidth: 560 }}
        onCloseClick={() => setEditingLocationMatchId(undefined)}
      >
        {editingLocationMatch && (
          <div className="tcsChoiceModal__body">
            <header>
              <span>Match location</span>
              <small>Change the location for this match only.</small>
            </header>
            <Autocomplete
              searchFunction={text => getPostcodes(text, user, true, tournament.schools)}
              getElementTitle={getNameByPlaceOrPostcode}
              customClass="mFullWidth"
              placeholder="Search for a location"
              defaultItem={editingLocationMatch.venue}
              onSelect={value => {
                patchMatch(editingLocationMatch.id!, { venue: structureVenue(value) });
                setEditingLocationMatchId(undefined);
              }}
            />
            <button
              type="button"
              className="tcsChoiceModal__default"
              onClick={() => {
                patchMatch(editingLocationMatch.id!, { venue: undefined });
                setEditingLocationMatchId(undefined);
              }}
            >
              Use tournament location · {tournamentVenueName}
            </button>
          </div>
        )}
      </SimpleModal>
      <SimpleModal
        isOpen={editingQualifierSlot !== undefined || (!!editingMatch && !!editingSlot)}
        title={editingQualifierSlot !== undefined ? 'Choose qualifier entrant' : matchContextTitle(editingMatch)}
        showFooter={false}
        customClass="tcsChoiceModal"
        zIndex={2100}
        dialogStyle={{ maxWidth: 680 }}
        onCloseClick={() => {
          setEditingSlot(undefined);
          setEditingQualifierSlot(undefined);
        }}
      >
        <div className="tcsSourcePicker is-modal">
          <header>
            <strong>Choose participant source</strong>
            <button type="button" className="tcsSourcePicker__bye" onClick={() => pickSource({ sourceType: 'BYE' })}>
              Use a bye
            </button>
          </header>
          <div>
            {customPool && (
              <section>
                <span>Competition entrants</span>
                <div>
                  {customPool.sources.map((source, index) => (
                    <button type="button" key={index} onClick={() => pickSource(source)}>
                      {sourceLabel(source)}
                    </button>
                  ))}
                </div>
              </section>
            )}
            {sourceGroups
              .filter(
                group =>
                  !customPool &&
                  earlierSourceGroup(
                    group.id,
                    editingQualifierSlot !== undefined ? editingQualifierId || editingBracketId : editingMatch?.groupId
                  )
              )
              .map(group => (
                <section key={group.id}>
                  <span>{group.name}</span>
                  <div>
                    {positionsForGroup(group.id).map(position => (
                      <button
                        type="button"
                        key={position}
                        onClick={() =>
                          pickSource({
                            sourceType: 'GROUP_POSITION',
                            sourceGroupId: group.id,
                            sourcePosition: position
                          })
                        }
                      >
                        {ordinalLabel(position)}
                      </button>
                    ))}
                    {!qualifierIds.has(group.id) && (
                      <button
                        type="button"
                        onClick={() =>
                          setPositionOverrides(current => ({
                            ...current,
                            [group.id]: positionsForGroup(group.id).length + 1
                          }))
                        }
                      >
                        + Add place
                      </button>
                    )}
                  </div>
                </section>
              ))}
            {!customPool && !!runtime?.teams.length && (
              <section className="tcsSourcePicker__teams">
                <span>Specific team</span>
                <input
                  type="search"
                  aria-label="Search teams or schools"
                  placeholder="Search teams or schools"
                  value={teamPickerQuery}
                  onChange={event => setTeamPickerQuery(event.target.value)}
                />
                <div>
                  {runtime.teams.filter(team => {
                    const identity = teamIdentity(team.id);
                    const label = `${identity.schoolName} ${identity.teamName}`.toLowerCase();
                    return teamPickerQuery.trim().toLowerCase().split(/\s+/).every(word => label.includes(word));
                  }).map(team => {
                    const identity = teamIdentity(team.id);
                    return (
                      <button
                        type="button"
                        key={team.id}
                        onClick={() => pickSource({ sourceType: 'TEAM', teamId: team.id })}
                      >
                        <SchoolMark name={identity.schoolName} pic={identity.schoolPic} />
                        <span>
                          <b>{identity.schoolName}</b>
                          <small>{identity.teamName}</small>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}
          </div>
          <div className="tcsSourcePicker__matches">
            <span>Previous matches</span>
            {matchesByStartTime
              .filter(
                item =>
                  !customPool &&
                  item.id !== editingMatch?.id &&
                  (canUseMatchSource(
                    { sourceType: 'MATCH_WINNER', sourceMatchId: item.id },
                    sourceDestinationGroupId,
                    editingMatch?.round
                  ) ||
                    canUseMatchSource(
                      { sourceType: 'MATCH_LOSER', sourceMatchId: item.id },
                      sourceDestinationGroupId,
                      editingMatch?.round
                    ))
              )
              .map(item => (
                <div key={item.id}>
                  <strong>{dynamicMatchName(item)}</strong>
                  {canUseMatchSource(
                    { sourceType: 'MATCH_WINNER', sourceMatchId: item.id },
                    sourceDestinationGroupId,
                    editingMatch?.round
                  ) && (
                    <button
                      type="button"
                      onClick={() => pickSource({ sourceType: 'MATCH_WINNER', sourceMatchId: item.id })}
                    >
                      Winner
                    </button>
                  )}
                  {!isByeMatch(item) &&
                    canUseMatchSource(
                      { sourceType: 'MATCH_LOSER', sourceMatchId: item.id },
                      sourceDestinationGroupId,
                      editingMatch?.round
                    ) && (
                      <button
                        type="button"
                        onClick={() => pickSource({ sourceType: 'MATCH_LOSER', sourceMatchId: item.id })}
                      >
                        Loser
                      </button>
                    )}
                </div>
              ))}
          </div>
        </div>
      </SimpleModal>
      <SimpleModal isOpen={!!replacingMatchId} isBare onBackdropClick={() => !saving && setReplacingMatchId(undefined)}>
        <section className="tcsReplaceFixture" role="alertdialog" aria-modal="true">
          <header>
            <span aria-hidden="true">!</span>
            <div>
              <h2>Replace this fixture?</h2>
              <p>
                Mark this fixture for replacement in the draft. It will be replaced only after you review and publish
                the changes.
              </p>
            </div>
          </header>
          <p>Any players, result or event-specific changes already entered on the existing event will be removed.</p>
          <footer>
            <button type="button" disabled={saving} onClick={() => setReplacingMatchId(undefined)}>
              Keep existing fixture
            </button>
            <button
              type="button"
              className="is-danger"
              disabled={saving}
              onClick={() => {
                const match = definition.matches.find(item => item.id === replacingMatchId);
                if (match) void materializeMatch(match, true);
              }}
            >
              {saving ? 'Replacing…' : 'Replace fixture'}
            </button>
          </footer>
        </section>
      </SimpleModal>
      <SimpleModal
        isOpen={recreatingRound}
        isBare
        onBackdropClick={() => {
          if (!saving) {
            setRecreatingRound(false);
            setPendingQualifierDefinition(undefined);
          }
        }}
      >
        <section className="tcsReplaceFixture" role="alertdialog" aria-modal="true">
          <header>
            <span aria-hidden="true">↻</span>
            <div>
              <h2>Recreate {activeBracket?.name} fixtures?</h2>
              <p>
                Rebuild this competition from its current entrants and timing defaults. Match overrides, removed flags
                and custom matches will be cleared when applicable.
              </p>
            </div>
          </header>
          <p>
            Mark this round for recreation in the draft. Existing fixtures and results stay active until you review and
            publish the changes.
          </p>
          <footer>
            <button
              type="button"
              disabled={saving}
              onClick={() => {
                setRecreatingRound(false);
                setPendingQualifierDefinition(undefined);
              }}
            >
              Cancel
            </button>
            <button type="button" className="is-danger" disabled={saving} onClick={() => void recreateSelectedRound()}>
              {saving ? 'Recreating…' : 'Recreate fixtures'}
            </button>
          </footer>
        </section>
      </SimpleModal>
      <UnsavedChangesModal
        isOpen={discardWarningOpen}
        message="You have unsaved changes to the structure or a group order. Leaving will discard them."
        onKeepEditing={() => {
          pendingNavigation.current = undefined;
          setDiscardWarningOpen(false);
        }}
        onDiscard={discardAndLeave}
      />
      <SimpleModal
        isOpen={!!editingQualifierId}
        title={String(structureGroups.find(group => group.id === editingQualifierId)?.name || 'Qualifier group')}
        showFooter={false}
        customClass="tcsChoiceModal"
        dialogStyle={{ maxWidth: 760 }}
        onCloseClick={() => setEditingQualifierId(undefined)}
      >
        <div className="tcsSourcePicker is-modal tcsQualifierEditor">
          <header>
            <strong>Choose entrants by finishing place or another source</strong>
          </header>
          <p>Select any group place, a specific team, a previous match winner or loser, or a bye.</p>
          <div>
            {sourceGroups
              .filter(group => earlierSourceGroup(group.id, editingQualifierId))
              .map(group => (
                <section key={group.id}>
                  <span>{group.name}</span>
                  <div>
                    {positionsForGroup(group.id).map(position => {
                      const source: TournamentMatchSource = {
                        sourceType: 'GROUP_POSITION',
                        sourceGroupId: group.id,
                        sourcePosition: position
                      };
                      const selected = qualifierSources.some(item => sourceKey(item) === sourceKey(source));
                      return (
                        <button
                          type="button"
                          className={selected ? 'is-selected' : ''}
                          aria-pressed={selected}
                          key={position}
                          onClick={() =>
                            setQualifierSources(current =>
                              selected
                                ? current.filter(item => sourceKey(item) !== sourceKey(source))
                                : [...current, source]
                            )
                          }
                        >
                          {selected ? '✓ ' : ''}
                          {ordinalLabel(position)}
                        </button>
                      );
                    })}
                    {!qualifierIds.has(group.id) && (
                      <button
                        type="button"
                        onClick={() =>
                          setPositionOverrides(current => ({
                            ...current,
                            [group.id]: positionsForGroup(group.id).length + 1
                          }))
                        }
                      >
                        + Add place
                      </button>
                    )}
                  </div>
                </section>
              ))}
          </div>
          <div className="tcsQualifierEntrants">
            {qualifierSources.map((source, index) => (
              <div key={index}>
                <span>{sourceLabel(source)}</span>
                <button type="button" onClick={() => setEditingQualifierSlot(index)}>
                  Change
                </button>
                <button
                  type="button"
                  aria-label={`Move entrant ${index + 1} up`}
                  disabled={!index}
                  onClick={() =>
                    setQualifierSources(current => {
                      const sources = [...current];
                      [sources[index - 1], sources[index]] = [sources[index], sources[index - 1]];
                      return sources;
                    })
                  }
                >
                  ↑
                </button>
                <button
                  type="button"
                  aria-label={`Move entrant ${index + 1} down`}
                  disabled={index === qualifierSources.length - 1}
                  onClick={() =>
                    setQualifierSources(current => {
                      const sources = [...current];
                      [sources[index], sources[index + 1]] = [sources[index + 1], sources[index]];
                      return sources;
                    })
                  }
                >
                  ↓
                </button>
                <button
                  type="button"
                  aria-label={`Remove entrant ${index + 1}`}
                  onClick={() => setQualifierSources(current => current.filter((_, i) => i !== index))}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <div className="tcsQualifierActions">
            <button type="button" onClick={() => setEditingQualifierSlot(qualifierSources.length)}>
              + Add participant source
            </button>
          </div>
          <div className="tcsQualifierTiming">
            <DatePicker
              label="Date"
              value={new Date(qualifierTiming.startTime)}
              clearable={false}
              onChange={value => {
                if (!value) return;
                const next = new Date(qualifierTiming.startTime);
                const date = new Date(value as Date);
                next.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
                setQualifierTiming(current => ({ ...current, startTime: next.toISOString() }));
              }}
            />
            <TimePicker
              label="First match starts"
              value={new Date(qualifierTiming.startTime)}
              clearable={false}
              minuteStep={5}
              onChange={value => {
                if (!value) return;
                const next = new Date(qualifierTiming.startTime);
                next.setHours(value.getHours(), value.getMinutes(), 0, 0);
                setQualifierTiming(current => ({ ...current, startTime: next.toISOString() }));
              }}
            />
            <label>
              <span>Match duration (min)</span>
              <input
                type="number"
                min="1"
                max="180"
                value={qualifierTiming.duration}
                onChange={event =>
                  setQualifierTiming(current => ({ ...current, duration: Number(event.target.value) }))
                }
              />
            </label>
            <label>
              <span>Break between matches (min)</span>
              <input
                type="number"
                min="0"
                max="180"
                value={qualifierTiming.break}
                onChange={event => setQualifierTiming(current => ({ ...current, break: Number(event.target.value) }))}
              />
            </label>
            <label>
              <span>Pitch</span>
              <select
                aria-label="Pitch"
                value={qualifierTiming.pitchId}
                onChange={event => setQualifierTiming(current => ({ ...current, pitchId: event.target.value }))}
              >
                <option value="">Not set</option>
                {(tournament.pitches || []).map(pitch => (
                  <option key={pitch.id} value={pitch.id}>
                    {pitch.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p>
            {qualifierEntrantCount} entrants · {qualifierMatchCount} matches. Each next match starts after the duration
            and break. Byes create no events.
          </p>
          {qualifierMatchCount > 0 && Number.isFinite(+qualifierFinish) && (
            <p role={+qualifierFinish > +new Date(tournament.endTime) ? 'alert' : undefined}>
              Expected finish:{' '}
              {qualifierFinish.toLocaleString('en-GB', {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit'
              })}
              {+qualifierFinish > +new Date(tournament.endTime)
                ? ' · The schedule extends beyond the tournament finish time.'
                : ''}
            </p>
          )}
          <div className="tcsQualifierActions">
            <button
              type="button"
              disabled={qualifierSources.filter(source => source.sourceType !== 'BYE').length < 2}
              onClick={applyQualifier}
            >
              Apply entrants & generate matches
            </button>
          </div>
        </div>
      </SimpleModal>
    </>
  );
  const editorFooter = (
    <>
      {' '}
      <footer>
        <button type="button" onClick={leave}>
          {standalone ? 'Back' : 'Cancel'}
        </button>
        <button type="button" disabled={saving || scheduleDirty || scheduleSaving || !runtime || !dirty} onClick={() => save(false)}>
          Save draft
        </button>
        <button
          type="button"
          className="is-primary"
          disabled={saving || scheduleDirty || scheduleSaving || !runtime || (!dirty && status === 'PUBLISHED')}
          onClick={() => save(true)}
        >
          {saving ? 'Saving…' : status === 'PUBLISHED' && dirty ? 'Publish changes' : 'Publish structure'}
        </button>
      </footer>
    </>
  );
  if (tournament.type && tournament.type !== 'TEAM_TOURNAMENT') return null;
  if (standalone)
    return (
      <TournamentStructureWorkspace
        tournament={{ ...tournament, tournamentLayout: structureStages }}
        definition={definition}
        runtime={runtime}
        groups={treeGroups}
        onMoveGroup={(groupId, direction) => {
          const group = treeGroups.find(item => item.id === groupId);
          if (!group) return;
          const siblings = treeGroups.filter(item => item.stageId === group.stageId);
          const neighbor = siblings[siblings.findIndex(item => item.id === groupId) + direction];
          if (!neighbor) return;
          setDefinition(current => {
            const order = orderByIds(naturalGroups, current.groupOrder, (a, b) =>
              a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
            ).map(item => item.id);
            const from = order.indexOf(groupId);
            const to = order.indexOf(neighbor.id);
            [order[from], order[to]] = [order[to], order[from]];
            return { ...current, groupOrder: order };
          });
        }}
        published={hasPublished}
        dirty={dirty || (status !== 'PUBLISHED' && hasPublished)}
        tab={workspaceTab}
        onTab={setWorkspaceTab}
        selectedGroupId={activeInitialGroup?.id || activeBracket?.id}
        onGroup={id => {
          setActiveBracketId(id);
          setActiveRound(undefined);
        }}
        onBack={leave}
        schedule={
          <TournamentScheduleGrid
            user={user}
            tournament={tournament}
            matches={definition.matches.filter(match => !isByeMatch(match)).map(match => ({
              ...match, name: fixtureDisplayName(match)
            }))}
            eventByMatch={new Map(runtime?.matches.map(match => [match.matchId, match.eventId]))}
            matchLabel={match => (['homeSource', 'awaySource'] as const).map((side, index) => {
              const source = match[side];
              const team = source.sourceType === 'TEAM' ? sourceTeamIdentity(source) :
                resolvedTeamIdentity(match.id, index === 0 ? 'home' : 'away');
              if (sourceGroups.some(group =>
                group.id === match.groupId && groupStageIds.has(group.stageId)) && !qualifierIds.has(match.groupId) &&
                (!match.round || match.round === 'GROUP')) return team ? [team.schoolName, team.teamName].filter(Boolean).join(' · ') : 'School to be confirmed';
              const origin = index === 0 ? match.homeOriginLabel : match.awayOriginLabel;
              return team ? [team.schoolName, team.teamName, origin].filter(Boolean).join(' · ') : origin || sourceLabel(source);
            }).join(' v ')}
            defaultDuration={matchDuration}
            onDirty={setScheduleDirty}
            onSaving={setScheduleSaving}
            onEventsSaved={positions => {
              setStoredDefinition(current => applySchedule(current, positions));
              setSavedDefinition(current => applySchedule(current, positions));
            }}
            onSaveDraft={async positions => !!await save(false, applySchedule(definition, positions))}
          />
        }
        rules={
          <>
            {timingPanel}
            {rankingPanel}
          </>
        }
        builder={
          <>
            {sourceIssues.length > 0 && (
              <div className="tswSourceWarnings" role="alert">
                <strong>Review participant sources</strong>
                <ul>
                  {sourceIssues.map((issue, index) => (
                    <li key={index}>{issue}</li>
                  ))}
                </ul>
              </div>
            )}
            {activeInitialGroup ? (
              <TournamentInitialGroupFixtures
                user={user}
                tournament={tournament}
                runtime={runtime}
                group={activeInitialGroup}
                definition={definition}
                defaultDuration={scheduleDefaults.groupDuration}
                onSaving={setScheduleSaving}
                onScheduleChange={(id, position, published) => {
                  setStoredDefinition(current => applySchedule(current, { [id]: position }));
                  if (published) setSavedDefinition(current => applySchedule(current, { [id]: position }));
                }}
              />
            ) : (
              builderPanel
            )}
          </>
        }
        matchday={
          <>
            {reviewPanel}
            {knockoutReadiness}
          </>
        }
        pendingGroupIds={Object.keys(manualOrders)}
        busy={saving || scheduleSaving}
        onRefresh={() => void refreshRuntime()}
        publicPage={
          <>
            {publicPanel}
            {displayPanel}
          </>
        }
        footer={editorFooter}
        onAddStage={() => setAddStageOpen(true)}
        groupBoard={(stageId, chooseCompetition) => {
          const rule = definition.stageRules?.find(item => item.stageId === stageId);
          return (
            <>
              {bulkPitchControl(treeGroups.filter(group => group.stageId === stageId).map(group => group.id))}
              <TournamentStructureGroupsBoard
                tournament={tournament}
                runtime={runtime}
                stageId={stageId}
                layout={definition.layout || initialStructureLayout(tournament, runtime)}
                update={updateLayout}
                onCompetition={chooseCompetition}
                rankingEditor={
                  <details className="tswStageRanking">
                    <summary>Stage ranking · {rule ? 'Custom rules' : 'Tournament default'}</summary>
                    <label>
                      <input
                        type="checkbox"
                        checked={!!rule}
                        onChange={event =>
                          setDefinition(current => ({
                            ...current,
                            stageRules: [
                              ...(current.stageRules || []).filter(item => item.stageId !== stageId),
                              ...(event.target.checked
                                ? [{ stageId, points: rankingPoints, tieBreakers: current.tieBreakers }]
                                : [])
                            ]
                          }))
                        }
                      />
                      Use custom ranking for this stage
                    </label>
                    {rule && (
                      <TournamentRankingEditor
                        points={rule.points}
                        criteria={rule.tieBreakers}
                        onChange={(points, tieBreakers) =>
                          setDefinition(current => ({
                            ...current,
                            stageRules: (current.stageRules || []).map(item =>
                              item.stageId === stageId ? { stageId, points, tieBreakers } : item
                            )
                          }))
                        }
                      />
                    )}
                  </details>
                }
              />
            </>
          );
        }}
        overlays={
          <>
            <SimpleModal
              isOpen={!!editingBracketId}
              title="Generate a knockout bracket"
              showFooter={false}
              customClass="tcsChoiceModal"
              dialogStyle={{ maxWidth: 680 }}
              onCloseClick={() => setEditingBracketId(undefined)}
            >
              <div className="tswModalContent">
                <label>
                  First round
                  <select
                    value={bracketFirstRound}
                    onChange={event => {
                      setBracketFirstRound(event.target.value);
                      const size = BRACKET_ROUNDS.find(round => round.id === event.target.value)!.slots;
                      setQualifierSources(current =>
                        Array.from({ length: size }, (_, index) => current[index] || { sourceType: 'TEAM' })
                      );
                    }}
                  >
                    {BRACKET_ROUNDS.map(round => (
                      <option key={round.id} value={round.id}>
                        {round.label}
                      </option>
                    ))}
                  </select>
                </label>
                <p>
                  Choose the first-round entrants. Later rounds link to the previous winners automatically. A bye
                  advances its opponent without creating an event.
                </p>
                {qualifierSources.map((source, index) => (
                  <button type="button" key={index} onClick={() => setEditingQualifierSlot(index)}>
                    {index + 1}.{' '}
                    {source.sourceType === 'TEAM' && !source.teamId ? 'Choose entrant' : sourceLabel(source)}
                  </button>
                ))}
                <footer>
                  <button type="button" onClick={() => setEditingBracketId(undefined)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={qualifierSources.some(source => source.sourceType === 'TEAM' && !source.teamId)}
                    onClick={() => {
                      try {
                        const group = structureGroups.find(item => item.id === editingBracketId)!;
                        const matches = knockoutFixtures(
                          group.id,
                          group.name,
                          bracketFirstRound,
                          qualifierSources,
                          tournament.startTime,
                          scheduleDefaults.knockoutDuration,
                          scheduleDefaults.knockoutBreak,
                          group.pitchId
                        );
                        setDefinition(current => ({
                          ...current,
                          layout: current.layout || initialStructureLayout(tournament, runtime),
                          matches: [...current.matches, ...matches]
                        }));
                        setActiveBracketId(group.id);
                        setActiveRound(bracketFirstRound);
                        setEditingBracketId(undefined);
                        setError('');
                      } catch (e) {
                        setError(e.message);
                      }
                    }}
                  >
                    Generate matches
                  </button>
                </footer>
              </div>
            </SimpleModal>
            {error && (
              <div className="tcsEditor__error" role="alert">
                {error}
              </div>
            )}
            {editorOverlays}
            <SimpleModal
              isOpen={!!publishPreview}
              title="Publish changes?"
              showFooter={false}
              customClass="tcsChoiceModal tswPublishModal"
              dialogStyle={{ maxWidth: 600 }}
              onCloseClick={() => !saving && setPublishPreview(undefined)}
            >
              {publishPreview && (
                <div className="tswModalContent">
                  <p>
                    Review the impact on existing fixtures before publishing. Replaced events are archived with their
                    results.
                  </p>
                  {publishPreview.rows.length > 0 && (
                    <ul className="tswPublishImpact">
                      {publishPreview.rows.map(row => (
                        <li key={row.groupId}>
                          <strong>{row.name}</strong>
                          <span>
                            {row.plannedMatches} planned matches · {row.fixturesRemoved} existing fixtures replaced ·{' '}
                            <span className={row.resultsRemoved ? 'is-warning' : undefined}>
                              {row.resultsRemoved} results archived
                            </span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {publishPreview.resultsRemoved > 0 && (
                    <p role="alert">
                      {publishPreview.resultsRemoved} existing results will be removed from the active tournament. Newly
                      created fixtures will need new results.
                    </p>
                  )}
                  {!publishPreview.rows.length && <p>No fixtures need to be replaced.</p>}
                  <footer>
                    <button type="button" disabled={saving} onClick={() => setPublishPreview(undefined)}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="is-primary"
                      disabled={saving}
                      onClick={() => void publishReviewed()}
                    >
                      {saving ? 'Publishing…' : 'Publish'}
                    </button>
                  </footer>
                </div>
              )}
            </SimpleModal>
            <SimpleModal
              isOpen={addStageOpen}
              title="Add a stage"
              showFooter={false}
              customClass="tcsChoiceModal"
              onCloseClick={() => setAddStageOpen(false)}
            >
              <div className="tswStagePresets">
                {[
                  {
                    name: 'Group stage · 4 groups',
                    type: 'GROUP',
                    names: ['Group A', 'Group B', 'Group C', 'Group D']
                  },
                  {
                    name: 'Group stage · 8 groups',
                    type: 'GROUP',
                    names: Array.from({ length: 8 }, (_, i) => `Group ${String.fromCharCode(65 + i)}`)
                  },
                  {
                    name: 'Placement groups',
                    type: 'QUALIFIER',
                    names: ['Cup A', 'Cup B', 'Plate A', 'Plate B', 'Bowl A', 'Bowl B', 'Trophy A', 'Trophy B']
                  },
                  { name: 'Finals', type: 'FINAL', names: ['Cup', 'Plate', 'Bowl', 'Trophy'] },
                  { name: 'Single knockout · Quarter-finals', type: 'FINAL', names: ['Cup'] },
                  { name: 'Empty stage', type: 'QUALIFIER', names: [] }
                ].map(preset => (
                  <button
                    type="button"
                    key={preset.name}
                    disabled={!runtime}
                    onClick={() => {
                      const id = structureId();
                      const groups = preset.names.map(name => ({ id: structureId(), name, stageId: id }));
                      const startTime = new Date(
                        +new Date(structureStages[structureStages.length - 1]?.startTime || tournament.startTime) +
                          60 * 60000
                      ).toISOString();
                      updateLayout(current => ({
                        ...current,
                        stages: [
                          ...current.stages,
                          {
                            id,
                            name: preset.name.split(' · ')[0],
                            type: preset.type as any,
                            format: preset.type === 'FINAL' ? 'KNOCKOUT' : 'ROUND_ROBIN',
                            startTime,
                            order: current.stages.length
                          }
                        ],
                        groups: [...current.groups, ...groups]
                      }));
                      if (preset.type === 'FINAL')
                        setDefinition(current => ({
                          ...current,
                          matches: [
                            ...current.matches,
                            ...groups.reduce(
                              (all, group) =>
                                all.concat(
                                  knockoutFixtures(
                                    group.id,
                                    group.name,
                                    preset.name.startsWith('Single') ? 'QUARTER_FINAL' : 'FINAL',
                                    Array.from({ length: preset.name.startsWith('Single') ? 8 : 2 }, (_, index) => ({
                                      sourceType: 'TEAM',
                                      label: `Empty slot ${index + 1}`
                                    })),
                                    startTime,
                                    scheduleDefaults.knockoutDuration,
                                    scheduleDefaults.knockoutBreak
                                  )
                                ),
                              [] as TournamentStructureMatch[]
                            )
                          ]
                        }));
                      if (preset.type === 'QUALIFIER' && groups.length)
                        setDefinition(current => {
                          const pools = groups.map(group => ({
                            groupId: group.id,
                            sources: Array.from({ length: 4 }, (_, index) => ({
                              sourceType: 'TEAM' as const,
                              label: `Empty slot ${index + 1}`
                            }))
                          }));
                          return {
                            ...current,
                            qualifierGroups: [...(current.qualifierGroups || []), ...pools],
                            matches: [
                              ...current.matches,
                              ...pools.reduce(
                                (all, pool) =>
                                  all.concat(
                                    roundRobinFixtures(
                                      pool.groupId,
                                      groups.find(group => group.id === pool.groupId)!.name,
                                      pool.sources,
                                      startTime,
                                      scheduleDefaults.groupDuration,
                                      scheduleDefaults.groupBreak
                                    )
                                  ),
                                [] as TournamentStructureMatch[]
                              )
                            ]
                          };
                        });
                      setAddStageOpen(false);
                    }}
                  >
                    <strong>{preset.name}</strong>
                    <span>{preset.names.length} competitions</span>
                  </button>
                ))}
              </div>
            </SimpleModal>
          </>
        }
      />
    );
  return (
    <>
      <button
        className="utmCompetitionTeams__tool"
        type="button"
        onClick={() =>
          history.push({ pathname: '/tournaments/manage/competition/structure', search: `tournament=${tournament.id}` })
        }
      >
        Tournament structure{' '}
        <span className={`tcsStatus is-${status.toLowerCase()}`}>{status === 'PUBLISHED' ? 'Published' : 'Draft'}</span>
      </button>
    </>
  );
};
